import React, { useState, useRef, useEffect } from 'react';
import {
	Container,
	Paper,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	Box,
	CircularProgress,
	Select,
	MenuItem,
	Tooltip,
	Popover,
	TextField,
	IconButton,
	FormControlLabel,
	Switch,
	ListItemIcon
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TodayIcon from '@mui/icons-material/Today';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RoundedCornerIcon from '@mui/icons-material/RoundedCorner';
import { QRCodeCanvas } from 'qrcode.react';
import MapIcon from '@mui/icons-material/Map';
import axios from 'axios';
import TemplateSelector from './TemplateSelector';
import { useUserState } from '../../hook/customerHook';
import { SketchPicker } from 'react-color';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/build/pdf.worker.min.mjs',
	import.meta.url
).toString();
const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		backgroundColor: '#f5f5f5',
		padding: 0,
		margin: 0,
		overflow: 'hidden'
	},
	mainContainer: {
		display: 'flex',
		flex: 1,
		gap: theme.spacing(1),
		overflow: 'hidden',
		padding: theme.spacing(1)
	},
	sidebar: {
		width: '280px',
		backgroundColor: 'white',
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[1],
		padding: theme.spacing(1),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
		height: '100%'
	},
	sidebarSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5)
	},
	sidebarTitle: {
		fontSize: '14px',
		color: '#666',
		marginBottom: theme.spacing(0.5),
		textTransform: 'uppercase',
		padding: theme.spacing(0.5)
	},
	sidebarButton: {
		justifyContent: 'flex-start',
		textAlign: 'left',
		padding: theme.spacing(0.75),
		marginBottom: theme.spacing(0.25),
		color: '#333',
		fontSize: '0.875rem',
		'& .MuiSvgIcon-root': {
			marginRight: theme.spacing(1)
		}
	},
	contentArea: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
		overflow: 'hidden'
	},
	topToolbar: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.5),
		backgroundColor: 'white',
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[1],
		flexWrap: 'nowrap',
		minHeight: '48px'
	},
	toolbarGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.25, 0.5),
		borderRight: `1px solid ${theme.palette.divider}`,
		'&:last-child': {
			borderRight: 'none',
			marginLeft: 'auto'
		}
	},
	toolbarButton: {
		padding: theme.spacing(0.25, 1),
		minWidth: 'unset',
		textTransform: 'none',
		fontSize: '0.75rem',
		height: '24px'
	},
	toolbarInput: {
		width: '50px',
		'& input': {
			padding: theme.spacing(0.25),
			fontSize: '0.75rem',
			height: '24px'
		}
	},
	toolbarLabel: {
		fontSize: '0.75rem',
		marginRight: theme.spacing(0.5),
		whiteSpace: 'nowrap'
	},
	canvasContainer: {
		position: 'relative',
		width: '100%',
		height: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: '#f5f5f5',
		border: '1px solid #ddd',
		overflow: 'auto',
		padding: '20px'
	},
	pdfContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		zIndex: 1
	},
	elementsContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		zIndex: 3,
		pointerEvents: 'none'
	},
	canvas: {
		position: 'absolute',
		top: 0,
		left: 0,
		zIndex: 2,
		pointerEvents: 'auto'
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(255, 255, 255, 0.8)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 3
	},
	errorOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(255, 0, 0, 0.8)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 3
	},
	letterBackground: {
		position: 'relative',
		width: (props) => `${props.width}px`,
		height: (props) => `${props.height}px`,
		backgroundColor: '#fff',
		boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
		border: '1px solid #ddd',
		margin: 'auto'
	},
	resizeHandle: {
		position: 'absolute',
		width: '12px',
		height: '12px',
		backgroundColor: '#0096FF',
		borderRadius: '50%',
		zIndex: 1003,
		border: '2px solid white',
		boxShadow: '0 0 4px rgba(0,0,0,0.2)'
	},
	'resize-nw': { top: '-6px', left: '-6px', cursor: 'nw-resize' },
	'resize-ne': { top: '-6px', right: '-6px', cursor: 'ne-resize' },
	'resize-sw': { bottom: '-6px', left: '-6px', cursor: 'sw-resize' },
	'resize-se': { bottom: '-6px', right: '-6px', cursor: 'se-resize' },
	element: {
		position: 'absolute',
		cursor: 'grab',
		userSelect: 'none',
		pointerEvents: 'auto',
		zIndex: 4
	},
	dragging: {
		cursor: 'grabbing',
		opacity: 0.8,
		zIndex: 1001
	},
	selected: {
		boxShadow: '0 0 0 2px #0096FF',
		zIndex: 1000
	},
	deleteButton: {
		position: 'absolute',
		top: '-15px',
		right: '-15px',
		backgroundColor: 'white',
		padding: '2px',
		borderRadius: '50%',
		zIndex: 9999,
		'&:hover': {
			backgroundColor: '#f5f5f5'
		},
		minWidth: 'unset',
		width: '25px',
		height: '25px'
	},
	text: {
		backgroundColor: 'white',
		border: '1px solid #ddd',
		minWidth: '150px',
		minHeight: '50px',
		whiteSpace: 'pre-wrap',
		overflowWrap: 'break-word'
	},
	image: {
		backgroundColor: 'white',
		border: '1px solid #ddd',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		borderRadius: '30px',
		boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
		width: '100%',
		height: '100%',
		position: 'relative'
	},
	imageContent: {
		width: '100%',
		height: '100%',
		objectFit: 'contain',
		borderRadius: '30px',
		padding: '8px',
		transition: 'all 0.1s ease'
	},
	uploadButton: {
		marginLeft: theme.spacing(1)
	},
	line: {
		backgroundColor: 'black',
		position: 'absolute',
		cursor: 'grab'
	},
	lineResizeHandle: {
		position: 'absolute',
		width: '10px',
		height: '10px',
		backgroundColor: '#0096FF',
		borderRadius: '50%',
		border: '2px solid white',
		zIndex: 1003
	},
	'line-start': {
		cursor: 'w-resize'
	},
	'line-end': {
		cursor: 'e-resize'
	},
	textFormatToolbar: {
		position: 'absolute',
		top: '-45px',
		left: '50%',
		transform: 'translateX(-50%)',
		backgroundColor: 'white',
		padding: '5px',
		borderRadius: '4px',
		boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
		display: 'flex',
		alignItems: 'center',
		gap: '5px',
		zIndex: 1002
	},
	formatButton: {
		padding: '4px',
		minWidth: 'unset',
		'&.active': {
			backgroundColor: '#e3f2fd'
		}
	},
	fontSelect: {
		width: '120px',
		'& .MuiSelect-select': {
			padding: theme.spacing(0.25),
			fontSize: '0.75rem',
			height: '24px'
		}
	},
	sizeSelect: {
		minWidth: '70px',
		fontSize: '14px'
	},
	saveButton: {
		marginLeft: 'auto',
		backgroundColor: '#1976d2',
		color: 'white',
		'&:hover': {
			backgroundColor: '#115293'
		}
	},
	icon: {
		backgroundColor: 'white',
		border: '1px solid #ddd',
		borderRadius: '50%',
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
	},
	zoomControls: {
		position: 'absolute',
		bottom: theme.spacing(2),
		right: theme.spacing(2),
		display: 'flex',
		gap: theme.spacing(1),
		backgroundColor: 'white',
		padding: theme.spacing(0.5),
		borderRadius: theme.shape.borderRadius,
		boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
		zIndex: 1000
	},
	zoomButton: {
		minWidth: '32px',
		width: '32px',
		height: '32px',
		padding: 0
	},
	zoomText: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(0, 1),
		fontSize: '0.875rem'
	},
	dynamicTextButton: {
		marginLeft: theme.spacing(1)
	},
	dynamicTextList: {
		minWidth: 200
	},
	dynamicTextItem: {
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	}
}));

const fontOptions = [
	'Arial',
	'Times New Roman',
	'Helvetica',
	'Georgia',
	'Courier New',
	'Verdana',
	'Roboto'
];

const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

const dynamicTextTypes = [
	{ type: 'date', label: 'Date', icon: TodayIcon, placeholder: '05/03/2020' },
	{ type: 'address', label: 'Address', icon: HomeIcon, placeholder: '((address))' },
	{ type: 'area', label: 'Area', icon: LocationCityIcon, placeholder: '((area))' },
	{ type: 'name', label: 'Name', icon: PersonIcon, placeholder: '((name))' },
	{ type: 'username', label: 'Username', icon: PersonIcon, placeholder: '((username))' },
	{ type: 'company', label: 'Company', icon: LocationCityIcon, placeholder: '((company))' },
	{ type: 'city', label: 'City', icon: LocationCityIcon, placeholder: '((city))' },
	{
		type: 'full_address',
		label: 'Full Address',
		icon: HomeIcon,
		placeholder: '((full_address))'
	},
	{
		type: 'current_date',
		label: 'Current Date',
		icon: TodayIcon,
		placeholder: '((current_date)) '
	}
];

// Constants for ReportLab coordinate system
const POINTS_TO_PIXELS = 1; // 1 point = 1.333333 pixels at 96 DPI
const PAGE_WIDTH_POINTS = 612; // 8.5 inches * 72 points per inch
const PAGE_HEIGHT_POINTS = 792; // 11 inches * 72 points per inch
const MARGIN_POINTS = 36; // 0.5 inches * 72 points per inch

// Convert ReportLab points to frontend pixels
const convertPointsToPixels = (points, offset = 0) => {
	return Math.round(points * (POINTS_TO_PIXELS + offset));
};

// Convert frontend pixels to ReportLab points
const convertPixelsToPoints = (pixels, offset = 0) => {
	return Math.round(pixels / (POINTS_TO_PIXELS + offset));
};

// Convert ReportLab coordinates (bottom-left origin) to frontend coordinates (top-left origin)
const convertReportLabToFrontend = (x, y, height = PAGE_HEIGHT_POINTS) => {
	console.log(height);
	return {
		x: convertPointsToPixels(x - 9),
		y: convertPointsToPixels(height - (y + 0))
	};
};

// Convert frontend coordinates (top-left origin) to ReportLab coordinates (bottom-left origin)
const convertFrontendToReportLab = (x, y, height = PAGE_HEIGHT_POINTS) => {
	console.log(height);
	return {
		x: convertPixelsToPoints(x + 9),
		y: height - convertPixelsToPoints(y + 0)
	};
};

const LetterDesigner = () => {
	const [pages, setPages] = useState([
		{
			elements: [],
			letterhead: null,
			letterheadSettings: {
				showDateOnHeader: false,
				startLetterFromTopPixel: 62,
				showYourNeighbourhoodTitle: true
			},
			dimensions: { width: 612, height: 792 }
		}
	]);
	const [currentPage, setCurrentPage] = useState(0);
	const [selectedId, setSelectedId] = useState(null);
	const [draggedId, setDraggedId] = useState(null);
	const [editingTextId, setEditingTextId] = useState(null);
	const [resizing, setResizing] = useState(null);
	const [loading, setLoading] = useState(false);
	const [showTemplateSelector, setShowTemplateSelector] = useState(true);
	const [currentTemplate, setCurrentTemplate] = useState(null);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [uploadError, setUploadError] = useState(null);
	const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
	const fileInputRef = useRef(null);
	const [tempDimensions, setTempDimensions] = useState({
		width: 816,
		height: 1056
	});
	const [canvasDimensions, setCanvasDimensions] = useState({
		width: 612,
		height: 792
	});
	const [letterhead, setLetterhead] = useState(null);
	const [letterheads, setLetterheads] = useState([]);
	const [letterheadSettings, setLetterheadSettings] = useState({
		showDateOnHeader: false,
		startLetterFromTopPixel: 62,
		showYourNeighbourhoodTitle: false
	});
	const containerRef = useRef(null);
	const [history, setHistory] = useState([]);
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
	const [showDynamicTypeSelector, setShowDynamicTypeSelector] = useState(false);
	const resizeInfo = useRef({
		handle: null,
		startX: 0,
		startY: 0,
		originalWidth: 0,
		originalHeight: 0,
		originalX: 0,
		originalY: 0
	});
	const dragInfo = useRef({
		startX: 0,
		startY: 0,
		originalX: 0,
		originalY: 0
	});
	const [userState] = useUserState();
	const [numPages, setNumPages] = useState(null);
	const [pdfError, setPdfError] = useState(null);
	const [isPdfLoading, setIsPdfLoading] = useState(false);
	const [pdfFile, setPdfFile] = useState(null);
	const canvasRef = useRef(null);
	const formattingToolbarRef = useRef(null);
	const [dynamicTextAnchorEl, setDynamicTextAnchorEl] = useState(null);
	const [selectedDynamicElementId, setSelectedDynamicElementId] = useState(null);
	const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
	const [currentLetterhead, setCurrentLetterhead] = useState(null);
	// Add state for color pickers
	const [showTextColorPicker, setShowTextColorPicker] = useState(false);
	const [showQrFgColorPicker, setShowQrFgColorPicker] = useState(false);
	const [showQrBgColorPicker, setShowQrBgColorPicker] = useState(false);
	const [showLineColorPicker, setShowLineColorPicker] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isUploadingLetterhead, setIsUploadingLetterhead] = useState(false);

	const classes = useStyles();

	// Fetch available letterheads
	useEffect(() => {
		const fetchLetterheads = async () => {
			try {
				const response = await axios.get('/api/letterheads');
				if (response.data) {
					setLetterheads(response.data);
				}
			} catch (error) {
				console.error('Error fetching letterheads:', error);
			}
		};
		fetchLetterheads();
	}, []);

	// Add click-away handler
	useEffect(() => {
		const handleClickOutside = (event) => {
			// Don't do anything if clicking on the formatting toolbar
			if (
				formattingToolbarRef.current &&
				formattingToolbarRef.current.contains(event.target)
			) {
				return;
			}

			// Don't do anything if clicking on a text element that's being edited
			if (editingTextId) {
				return;
			}

			// Don't do anything if clicking on a selected text element
			const clickedElement = event.target.closest(`[data-element-id]`);
			if (clickedElement && clickedElement.dataset.elementId === selectedId) {
				return;
			}

			// Clear selection and formatting toolbar when clicking outside
			setSelectedId(null);
			setShowFormattingToolbar(false);
			setShowDynamicTypeSelector(false);
			setEditingTextId(null);
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [editingTextId, selectedId]);

	const getDefaultText = (type) => {
		switch (type) {
			case 'qrCode':
				return 'QR Code';
			case 'cma':
				return 'Comparative Market Analysis';
			case 'marketUpdate':
				return 'Market Update';
			case 'text':
				return 'Double-click to edit';
			case 'image':
				return '';
			default:
				return 'New Element';
		}
	};

	const handleSelect = (id, e) => {
		e.stopPropagation();
		e.preventDefault();

		const element = pages[currentPage].elements.find((el) => el.id === id);
		if (!element) return;

		// If clicking the same element, don't deselect it
		if (selectedId === id) {
			return;
		}

		setSelectedId(id);
		if (element.type === 'text' || element.type === 'cma') {
			setShowFormattingToolbar(true);
		} else {
			setShowFormattingToolbar(false);
		}
	};

	const handleMouseDown = (e, id) => {
		if (e.button !== 0) return;
		const currentPageElements = pages[currentPage].elements;
		const element = currentPageElements.find((el) => el.id === id);
		if (!element) return;

		setDraggedId(id);
		setSelectedId(id);

		dragInfo.current = {
			startX: e.clientX,
			startY: e.clientY,
			originalX: element.x,
			originalY: element.y
		};

		e.stopPropagation();
	};

	const handleMouseMove = (e) => {
		if (!draggedId) return;

		const dx = e.clientX - dragInfo.current.startX;
		const dy = e.clientY - dragInfo.current.startY;

		setPages((prevPages) => {
			const newPages = [...prevPages];
			const currentPageElements = newPages[currentPage].elements;
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: currentPageElements.map((el) => {
					if (el.id === draggedId) {
						return {
							...el,
							x: dragInfo.current.originalX + dx,
							y: dragInfo.current.originalY + dy
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const handleMouseUp = () => {
		if (draggedId) {
			addToHistory([...pages[currentPage].elements]);
		}
		setDraggedId(null);
	};

	const handleTextChange = (e, elementId) => {
		const newText = e.target.value;
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) =>
					el.id === elementId ? { ...el, text: newText } : el
				)
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) =>
					el.id === elementId ? { ...el, text: newText } : el
				)
			};
			return newPages;
		});
	};

	const handleTextBlur = () => {
		setEditingTextId(null);
	};

	const handleDoubleClick = (element, e) => {
		e.stopPropagation();
		if (
			element.type === 'text' ||
			element.type === 'letterIntro' ||
			element.type === 'letterClosing' ||
			element.type === 'address' ||
			element.type === 'cma'
		) {
			setEditingTextId(element.id);
			// Set the cursor at the end of the text
			setTimeout(() => {
				const textarea = document.querySelector(`textarea[data-id="${element.id}"]`);
				if (textarea) {
					textarea.focus();
					textarea.setSelectionRange(element.text.length, element.text.length);
				}
			}, 0);
		}
	};

	const handleTemplateSelect = async (template) => {
		try {
			setIsLoadingTemplate(true);
			setCurrentLetterhead(null);

			// Parse settings JSON
			const settings =
				typeof template.settings === 'string'
					? JSON.parse(template.settings)
					: template.settings || {};

			setCurrentTemplate({ ...template, settings });
			setHistory([]);
			setCurrentIndex(-1);

			// Initialize pages array
			const initializedPages = [];

			// Helper to get dimensions from a letterhead fileName
			const getLetterheadDims = async (fileName) => {
				if (!fileName) return { width: 612, height: 792 };
				try {
					const response = await axios.get(`/api/letterheads/${fileName}`, {
						responseType: 'arraybuffer',
						headers: { Accept: 'application/pdf' }
					});
					if (response.data) {
						const base64 = btoa(
							new Uint8Array(response.data).reduce(
								(data, byte) => data + String.fromCharCode(byte),
								''
							)
						);
						const dataUrl = `data:application/pdf;base64,${base64}`;
						return await extractPdfPageSize(dataUrl);
					}
				} catch (e) {
					console.error('Failed to get letterhead dims', e);
				}
				return { width: 612, height: 792 };
			};

			// --- MAIN PAGE ---
			let mainDims = { width: 612, height: 792 };
			let mainLetterheadDataUrl = null;
			if (settings.fullLetterhead?.fileName) {
				const response = await axios.get(
					`/api/letterheads/${settings.fullLetterhead.fileName}`,
					{
						responseType: 'arraybuffer',
						headers: { Accept: 'application/pdf' }
					}
				);
				if (response.data) {
					const base64 = btoa(
						new Uint8Array(response.data).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							''
						)
					);
					mainLetterheadDataUrl = `data:application/pdf;base64,${base64}`;
					mainDims = await extractPdfPageSize(mainLetterheadDataUrl);
				}
			}

			const mainPage = {
				elements: [],
				letterhead: mainLetterheadDataUrl
					? {
							url: mainLetterheadDataUrl,
							fileName: settings.fullLetterhead.fileName
					  }
					: null,
				letterheadSettings: {
					showDateOnHeader: settings.fullLetterhead?.showDateOnHeader === 1,
					startLetterFromTopPixel:
						parseInt(settings.fullLetterhead?.startLetterFromTopPixel) || 62,
					showYourNeighbourhoodTitle:
						settings.fullLetterhead?.showYourNeighbourhoodTitle === 1
				},
				dimensions: mainDims,
				letterhead: mainLetterheadDataUrl
					? {
							url: mainLetterheadDataUrl,
							fileName: settings.fullLetterhead.fileName
					  }
					: null,
				letterheadSettings: {
					showDateOnHeader: settings.fullLetterhead?.showDateOnHeader === 1,
					startLetterFromTopPixel:
						parseInt(settings.fullLetterhead?.startLetterFromTopPixel) || 62,
					showYourNeighbourhoodTitle:
						settings.fullLetterhead?.showYourNeighbourhoodTitle === 1
				},
				dimensions: mainDims
			};

			// Add address element
			if (settings.Address) {
				let { x, y } = convertReportLabToFrontend(
					settings.Address.x_cordinate || 0,
					settings.Address.y_cordinate + (settings.Address.second_line_space + 20),
					mainDims.height
				);
				const secondLineSpace = settings.Address.second_line_space || 35;
				mainPage.elements.push({
					id: Date.now().toString() + '_address',
					type: 'address',
					x,
					y,
					width: 140,
					height: 70,
					text: '1234 Main Street\nVancouver, V5P 2H3',
					style: {
						fontSize: settings.Address.font_size || 12,
						fontFamily: 'Arial',
						color: settings.Address.color || '#000000',
						backgroundColor: 'transparent',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'break-word',
						secondLineSpace: secondLineSpace,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between'
					}
				});
			}

			// Add date element
			if (settings.Date) {
				let { x, y } = convertReportLabToFrontend(
					settings.Date.x_cordinate || 0,
					settings.Date.y_cordinate + 20,
					mainDims.height
				);
				mainPage.elements.push({
					id: Date.now().toString() + '_date',
					type: 'text',
					x,
					y,
					width: 100,
					height: 30,
					text: '2020-05-03',
					style: {
						fontSize: settings.Date.font_size || 12,
						fontFamily: 'Arial',
						color: settings.Date.color || '#000000',
						backgroundColor: 'transparent',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'break-word'
					}
				});
			}

			// Add QR code element
			if (settings.qr_code) {
				let { x, y } = convertReportLabToFrontend(
					settings.qr_code.x_coordinate + 9 || 0,
					settings.qr_code.y_coordinate + settings.qr_code.height,
					mainDims.height
				);
				mainPage.elements.push({
					id: Date.now().toString() + '_qr',
					type: 'qrCode',
					x,
					y,
					width: convertPointsToPixels(settings.qr_code.width || 90),
					height: convertPointsToPixels(settings.qr_code.height || 90),
					style: {
						qrColor: settings.qr_code.colour || '#000000',
						backgroundColor: settings.qr_code.bg_colour || '#FFFFFF'
					}
				});
			}

			// Add map element
			if (settings.Map) {
				let { x, y } = convertReportLabToFrontend(
					settings.Map.x_coordinate + 9 || 0,
					settings.Map.y_coordinate + convertPointsToPixels(settings.Map.height) || 0,
					mainDims.height
				);
				mainPage.elements.push({
					id: Date.now().toString() + '_map',
					type: 'map',
					x,
					y,
					width: convertPointsToPixels(settings.Map.width || 300),
					height: convertPointsToPixels(settings.Map.height || 200),
					activePins: settings.Map.number_of_active_properties || 3,
					soldPins: settings.Map.number_of_sold_properties || 2,
					style: {
						backgroundColor: '#e0e0e0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexDirection: 'column'
					}
				});
			}

			// Add market statistics element
			if (settings.market_stat) {
				let { x, y } = convertReportLabToFrontend(
					settings.market_stat.x_coordinate + 9 || 0,
					settings.market_stat.y_coordinate + settings.market_stat.height || 0,
					mainDims.height
				);
				mainPage.elements.push({
					id: Date.now().toString() + '_marketStat',
					type: 'marketStat',
					x,
					y,
					width: convertPointsToPixels(settings.market_stat.width || 200),
					height: convertPointsToPixels(settings.market_stat.height || 120),
					style: {
						backgroundColor: settings.market_stat.bg_colour || '#FFFFFF',
						color: settings.market_stat.colour || '#000000',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexDirection: 'column',
						border: '1px solid #e0e0e0'
					}
				});
			}

			// Add letter body elements
			if (settings.LetterBody) {
				if (settings.LetterBody.LetterNotSoldUsernameIntro !== undefined) {
					let { x, y } = convertReportLabToFrontend(
						settings.LetterBody.x_coordinate || 0,
						settings.LetterBody.y_coordinate || 0,
						mainDims.height
					);
					mainPage.elements.push({
						id: Date.now().toString() + '_intro',
						type: 'letterIntro',
						x,
						y,
						width: 500,
						height: 100,
						text: settings.LetterBody.LetterNotSoldUsernameIntro,
						style: {
							fontSize: settings.LetterBody.font_size || 12,
							fontFamily: 'Arial',
							color: '#000000',
							backgroundColor: 'transparent',
							whiteSpace: 'pre-wrap',
							overflowWrap: 'break-word'
						}
					});
				}

				if (settings.LetterBody.LetterUsernameClosing !== undefined) {
					let { x, y } = convertReportLabToFrontend(
						settings.LetterBody.closing_x_coordinate || 0,
						settings.LetterBody.closing_y_coordinate || 0,
						mainDims.height
					);
					mainPage.elements.push({
						id: Date.now().toString() + '_closing',
						type: 'letterClosing',
						x,
						y,
						width: 500,
						height: 100,
						text: settings.LetterBody.LetterUsernameClosing,
						style: {
							fontSize: settings.LetterBody.closing_font_size || 12,
							fontFamily: 'Arial',
							color: '#000000',
							backgroundColor: 'transparent',
							whiteSpace: 'pre-wrap',
							overflowWrap: 'break-word'
						}
					});
				}
			}
			if (settings.CMA) {
				let { x, y } = convertReportLabToFrontend(
					settings.CMA.x_cordinate || 0,
					settings.CMA.y_cordinate || 0,
					mainDims.height
				);
				mainPage.elements.push({
					id: Date.now().toString() + '_cma',
					type: 'cma',
					x,
					y,
					width: 500,
					height: 230,
					text:
						settings.CMA.your_neighbourhood_text ||
						'Recent Activity in Your Area\nSold Homes:\nA1: 2770 Euclid Ave — Sold Dec 2024 for $2,265,000 (Assessed: $2,212,700)\nA2: 3872 Nanaimo St — Sold Dec 2024 for $1,800,000\nA3: 2872 E 25th Ave — Sold Nov 2024 for $1,500,000 (Assessed: $1,548,500)\nActive Listings:\nS1: 2669 Vanness Ave — Listed at $2,600,000 (Assessed: $2,971,000)\nS2: 2780 E 26th Ave — Listed at $2,333,000',
					style: {
						fontSize: settings.CMA.font_size || 9,
						fontFamily: 'Arial',
						color: settings.CMA.text_color || '#212163',
						backgroundColor: 'transparent',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'break-word',
						lineHeight: '1.5'
					}
				});
			}

			// Add text elements
			if (settings.textElements) {
				settings.textElements.forEach((element) => {
					const { x_coordinate, y_coordinate, width, height, text, style } = element;
					const { x, y } = convertReportLabToFrontend(
						x_coordinate,
						y_coordinate,
						mainDims.height
					);
					mainPage.elements.push({
						id: Date.now().toString() + '_text',
						type: 'text',
						x,
						y,
						width: convertPointsToPixels(width),
						height: convertPointsToPixels(height),
						text,
						style: {
							fontSize: parseInt(style?.fontSize) || 12,
							fontSize: parseInt(style?.fontSize) || 12,
							fontFamily: style?.fontFamily || 'Arial',
							color: style?.color || '#000000',
							fontWeight: style?.fontWeight || 'normal',
							fontStyle: style?.fontStyle || 'normal',
							textDecoration: style?.textDecoration || 'none'
						}
					});
				});
			}

			initializedPages.push(mainPage);

			// --- ADDITIONAL PAGES ---
			for (let i = 1; ; i++) {
				const pageKey = `page${i + 1}`;
				const pageSettings = settings[pageKey];
				if (!pageSettings) break;

				let pageDims = { width: 612, height: 792 };
				let pageLetterheadDataUrl = null;
				if (pageSettings.fullLetterhead?.fileName) {
					const response = await axios.get(
						`/api/letterheads/${pageSettings.fullLetterhead.fileName}`,
						{
							responseType: 'arraybuffer',
							headers: { Accept: 'application/pdf' }
						}
					);
					if (response.data) {
						const base64 = btoa(
							new Uint8Array(response.data).reduce(
								(data, byte) => data + String.fromCharCode(byte),
								''
							)
						);
						pageLetterheadDataUrl = `data:application/pdf;base64,${base64}`;
						pageDims = await extractPdfPageSize(pageLetterheadDataUrl);
					}
				}

				const page = {
					elements: [],
					letterhead: pageLetterheadDataUrl
						? {
								url: pageLetterheadDataUrl,
								fileName: pageSettings.fullLetterhead.fileName
						  }
						: null,
					letterheadSettings: {
						showDateOnHeader: pageSettings.fullLetterhead?.showDateOnHeader === 1,
						startLetterFromTopPixel:
							parseInt(pageSettings.fullLetterhead?.startLetterFromTopPixel) || 98,
						showYourNeighbourhoodTitle:
							pageSettings.fullLetterhead?.showYourNeighbourhoodTitle === 1
					},
					dimensions: pageDims,
					letterhead: pageLetterheadDataUrl
						? {
								url: pageLetterheadDataUrl,
								fileName: pageSettings.fullLetterhead.fileName
						  }
						: null,
					letterheadSettings: {
						showDateOnHeader: pageSettings.fullLetterhead?.showDateOnHeader === 1,
						startLetterFromTopPixel:
							parseInt(pageSettings.fullLetterhead?.startLetterFromTopPixel) || 98,
						showYourNeighbourhoodTitle:
							pageSettings.fullLetterhead?.showYourNeighbourhoodTitle === 1
					},
					dimensions: pageDims
				};

				// Add CMA element
				if (pageSettings.CMA) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.CMA.x_cordinate || 0,
						pageSettings.CMA.y_cordinate || 0,
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_cma',
						type: 'cma',
						x,
						y,
						width: 500,
						height: 230,
						text:
							pageSettings.CMA.your_neighbourhood_text ||
							'Recent Activity in Your Area\nSold Homes:\nA1: 2770 Euclid Ave — Sold Dec 2024 for $2,265,000 (Assessed: $2,212,700)\nA2: 3872 Nanaimo St — Sold Dec 2024 for $1,800,000\nA3: 2872 E 25th Ave — Sold Nov 2024 for $1,500,000 (Assessed: $1,548,500)\nActive Listings:\nS1: 2669 Vanness Ave — Listed at $2,600,000 (Assessed: $2,971,000)\nS2: 2780 E 26th Ave — Listed at $2,333,000',
						style: {
							fontSize: pageSettings.CMA.font_size || 9,
							fontFamily: 'Arial',
							color: pageSettings.CMA.text_color || '#212163',
							backgroundColor: 'transparent',
							whiteSpace: 'pre-wrap',
							overflowWrap: 'break-word',
							lineHeight: '1.5'
						}
					});
				}

				// Add map element for additional pages
				if (pageSettings.Map) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.Map.x_coordinate + 9 || 0,
						pageSettings.Map.y_coordinate +
							convertPointsToPixels(pageSettings.Map.height) || 0,
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_map',
						type: 'map',
						x,
						y,
						width: convertPointsToPixels(pageSettings.Map.width || 300),
						height: convertPointsToPixels(pageSettings.Map.height || 200),
						activePins: pageSettings.Map.number_of_active_properties || 3,
						soldPins: pageSettings.Map.number_of_sold_properties || 2,
						style: {
							backgroundColor: '#e0e0e0',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexDirection: 'column'
						}
					});
				}

				// Add market statistics element for additional pages
				if (pageSettings.market_stat) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.market_stat.x_coordinate + 9 || 0,
						pageSettings.market_stat.y_coordinate + pageSettings.market_stat.height ||
							0,
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_marketStat',
						type: 'marketStat',
						x,
						y,
						width: convertPointsToPixels(pageSettings.market_stat.width || 200),
						height: convertPointsToPixels(pageSettings.market_stat.height || 120),
						style: {
							backgroundColor: pageSettings.market_stat.bg_colour || '#FFFFFF',
							color: pageSettings.market_stat.colour || '#000000',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexDirection: 'column',
							border: '1px solid #e0e0e0'
						}
					});
				}

				// Add date element
				if (pageSettings.Date) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.Date.x_cordinate || 0,
						pageSettings.Date.y_cordinate + 20,
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_date',
						type: 'text',
						type: 'text',
						x,
						y,
						width: 100,
						height: 30,
						text: '2020-05-03',
						style: {
							fontSize: pageSettings.Date.font_size || 6,
							fontFamily: 'Arial',
							color: pageSettings.Date.color || '#000000',
							color: pageSettings.Date.color || '#000000',
							backgroundColor: 'transparent',
							whiteSpace: 'pre-wrap',
							overflowWrap: 'break-word'
						}
					});
				}

				// Add address element
				if (pageSettings.Address) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.Address.x_cordinate || 0,
						pageSettings.Address.y_cordinate +
							(pageSettings.Address.second_line_space + 20),
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_address',
						type: 'address',
						x,
						y,
						width: 140,
						height: 70,
						text: '1234 Main Street\nVancouver, V5P 2H3',
						style: {
							fontSize: pageSettings.Address.font_size || 6,
							fontFamily: 'Arial',
							color: pageSettings.Address.color || '#000000',
							color: pageSettings.Address.color || '#000000',
							backgroundColor: 'transparent',
							whiteSpace: 'pre-wrap',
							overflowWrap: 'break-word',
							secondLineSpace: pageSettings.Address.second_line_space || 7,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between'
						}
					});
				}

				// add qr code
				if (pageSettings.qr_code) {
					let { x, y } = convertReportLabToFrontend(
						pageSettings.qr_code.x_coordinate + 9 || 0,
						pageSettings.qr_code.y_coordinate + pageSettings.qr_code.height,
						pageDims.height
					);
					page.elements.push({
						id: Date.now().toString() + '_qr',
						type: 'qrCode',
						x,
						y,
						width: convertPointsToPixels(pageSettings.qr_code.width || 90),
						height: convertPointsToPixels(pageSettings.qr_code.height || 90),
						style: {
							qrColor: pageSettings.qr_code.colour || '#000000',
							backgroundColor: pageSettings.qr_code.bg_colour || '#FFFFFF'
						}
					});
				}

				// Add letter body elements
				if (pageSettings.LetterBody) {
					if (pageSettings.LetterBody.LetterNotSoldUsernameIntro) {
						page.elements.push({
							id: Date.now().toString() + '_intro',
							type: 'letterIntro',
							x: 50,
							y: 200,
							width: 500,
							height: 100,
							text: pageSettings.LetterBody.LetterNotSoldUsernameIntro,
							style: {
								fontSize: 12,
								fontFamily: 'Arial',
								color: '#000000',
								backgroundColor: 'transparent',
								whiteSpace: 'pre-wrap',
								overflowWrap: 'break-word'
							}
						});
					}

					if (pageSettings.LetterBody.LetterUsernameClosing) {
						page.elements.push({
							id: Date.now().toString() + '_closing',
							type: 'letterClosing',
							x: 50,
							y: 400,
							width: 500,
							height: 100,
							text: pageSettings.LetterBody.LetterUsernameClosing,
							style: {
								fontSize: 12,
								fontFamily: 'Arial',
								color: '#000000',
								backgroundColor: 'transparent',
								whiteSpace: 'pre-wrap',
								overflowWrap: 'break-word'
							}
						});
					}
				}

				// Add blank LetterBody for additional pages if not present
				if (!pageSettings.LetterBody) {
					pageSettings.LetterBody = {
						LetterUsernameClosing: '',
						LetterNotSoldUsernameIntro: ''
					};
				}

				initializedPages.push(page);
			}

			setPages(initializedPages);
			setCurrentPage(0);
		} catch (error) {
			console.error('Error parsing template settings:', error);
			setPages([
				{
					elements: [],
					letterhead: null,
					letterheadSettings: {
						showDateOnHeader: false,
						startLetterFromTopPixel: 62,
						showYourNeighbourhoodTitle: true
					},
					dimensions: { width: 612, height: 792 }
				}
			]);
		} finally {
			setIsLoadingTemplate(false);
		}
		setShowTemplateSelector(false);
	};

	const handleNewTemplate = async (templateName, letterheadFileName) => {
		try {
			setIsCreatingTemplate(true);
			if (!userState || !userState.user) {
				setSaveError('User not authenticated');
				return;
			}

			// Reset letterhead so previous one is not carried over
			setLetterhead(null);
			setCurrentLetterhead(null);
			setCanvasDimensions({ width: 612, height: 792 });
			setTempDimensions({ width: 612, height: 792 });

			// If a letterhead was uploaded, split it into pages and upload each page
			let letterheadFileNames = [];
			if (letterheadFileName) {
				try {
					// Get the PDF file
					const response = await axios.get(`/api/letterheads/${letterheadFileName}`, {
						responseType: 'arraybuffer',
						headers: { Accept: 'application/pdf' }
					});

					// Convert to base64
					const base64 = btoa(
						new Uint8Array(response.data).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							''
						)
					);

					// Create a FormData object for each page
					const formData = new FormData();
					formData.append('file', new Blob([response.data], { type: 'application/pdf' }));
					formData.append('splitPages', 'true');

					// Upload the PDF to be split into pages
					const splitResponse = await axios.post(
						'/api/letterheads/split_and_upload',
						formData,
						{
							headers: {
								'Content-Type': 'multipart/form-data'
							}
						}
					);

					if (splitResponse.data.success) {
						letterheadFileNames = splitResponse.data.fileNames;
					} else {
						throw new Error('Failed to split PDF into pages');
					}
				} catch (error) {
					console.error('Error processing multi-page PDF:', error);
					setSaveError('Failed to process multi-page PDF. Please try again.');
					return;
				}
			}

			// Create initial settings with letterhead if provided
			const initialSettings = {
				fullLetterhead: letterheadFileNames[0]
					? {
							fileName: letterheadFileNames[0],
							showDateOnHeader: 0,
							showGreeting: 0,
							showAddressSection: 0,
							showYourHomeSection: 0,
							showComparableSection: 0,
							excludeYourHomeInLetter: 0,
							startLetterFromTopPixel: '62',
							showYourNeighbourhoodTitle: 0
					  }
					: null,
				LetterBody: {
					LetterUsernameClosing: '',
					LetterNotSoldUsernameIntro: ''
				},
				new_scaled: 1 // Add new_scaled to main page settings
			};

			// Add additional pages if we have more letterhead pages
			for (let i = 1; i < letterheadFileNames.length; i++) {
				initialSettings[`page${i + 1}`] = {
					fullLetterhead: {
						fileName: letterheadFileNames[i],
						showDateOnHeader: 0,
						showGreeting: 0,
						showAddressSection: 0,
						showYourHomeSection: 0,
						showComparableSection: 0,
						excludeYourHomeInLetter: 0,
						startLetterFromTopPixel: '98',
						showYourNeighbourhoodTitle: 0
					},
					LetterBody: {
						LetterUsernameClosing: '',
						LetterNotSoldUsernameIntro: ''
					},
					new_scaled: 1 // Add new_scaled to each additional page settings
				};
			}

			const templateData = {
				name: templateName,
				organization_id: 1,
				user_id: userState.user.user_id,
				settings: JSON.stringify(initialSettings)
			};

			const response = await axios.post('/api/letter_templates', templateData);
			if (response.status === 201) {
				setCurrentTemplate({
					id: response.data.id,
					name: templateName,
					settings: templateData.settings
				});

				// Initialize pages based on the number of letterhead pages
				const initialPages = [];
				for (let i = 0; i < Math.max(1, letterheadFileNames.length); i++) {
					initialPages.push({
						elements: [],
						letterhead: null,
						letterheadSettings: {
							showDateOnHeader: false,
							showGreeting: false,
							showAddressSection: false,
							showYourHomeSection: false,
							showComparableSection: false,
							excludeYourHomeInLetter: false,
							startLetterFromTopPixel: i === 0 ? 62 : 98,
							showYourNeighbourhoodTitle: false
						}
					});
				}

				setPages(initialPages);
				setCurrentPage(0);

				// Load letterheads for all pages
				for (let i = 0; i < letterheadFileNames.length; i++) {
					await handleLetterheadSelect(letterheadFileNames[i], i);
				}

				setShowTemplateSelector(false);
			} else {
				setSaveError('Failed to create template');
			}
		} catch (error) {
			console.error('Error creating template', error);
			setSaveError('Failed to create template. Please try again');
		} finally {
			setIsCreatingTemplate(false);
		}
	};

	const handleBackToTemplates = () => {
		setShowTemplateSelector(true);
		setCurrentTemplate(null);
		setHistory([]); // Reset history when going back to templates
		setCurrentIndex(-1); // Reset current index
	};

	const handleCanvasClick = (e) => {
		e.stopPropagation();

		// Only clear selection if clicking directly on canvas background
		const isCanvasBackground = e.target.classList.contains(classes.canvasContainer);
		if (isCanvasBackground) {
			setSelectedId(null);
			setShowFormattingToolbar(false);
			setEditingTextId(null);
		}
	};

	const handleResizeStart = (e, element, handle) => {
		e.stopPropagation();
		e.preventDefault();
		setResizing(element.id);
		resizeInfo.current = {
			handle,
			startX: e.clientX,
			startY: e.clientY,
			originalWidth: element.width || 150,
			originalHeight: element.height || 50,
			originalX: element.x,
			originalY: element.y
		};
	};

	const handleResize = (e) => {
		if (!resizing || !resizeInfo.current.handle) return;

		const currentPageElements = pages[currentPage].elements;
		const element = currentPageElements.find((el) => el.id === resizing);
		if (!element) return;

		const dx = e.clientX - resizeInfo.current.startX;
		const dy = e.clientY - resizeInfo.current.startY;

		setPages((prevPages) => {
			const newPages = [...prevPages];
			const currentPageElements = newPages[currentPage].elements;
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: currentPageElements.map((el) => {
					if (el.id === resizing) {
						if (el.type === 'line') {
							if (el.orientation === 'horizontal') {
								let newWidth = resizeInfo.current.originalWidth;
								let newX = resizeInfo.current.originalX;

								if (resizeInfo.current.handle === 'start') {
									newWidth -= dx;
									newX += dx;
								} else {
									newWidth += dx;
								}

								return {
									...el,
									width: Math.max(newWidth, 20),
									x: newX
								};
							} else {
								let newHeight = resizeInfo.current.originalHeight;
								let newY = resizeInfo.current.originalY;

								if (resizeInfo.current.handle === 'start') {
									newHeight -= dy;
									newY += dy;
								} else {
									newHeight += dy;
								}

								return {
									...el,
									height: Math.max(newHeight, 20),
									y: newY
								};
							}
						}

						// Special handling for QR code to maintain square aspect ratio
						if (el.type === 'qrCode') {
							let newWidth = resizeInfo.current.originalWidth;
							let newHeight = resizeInfo.current.originalHeight;
							let newX = resizeInfo.current.originalX;
							let newY = resizeInfo.current.originalY;

							const delta = Math.max(Math.abs(dx), Math.abs(dy));
							const sign = Math.sign(dx);

							switch (resizeInfo.current.handle) {
								case 'se':
									newWidth = Math.max(
										20,
										resizeInfo.current.originalWidth + delta * sign
									);
									newHeight = newWidth;
									break;
								case 'sw':
									newWidth = Math.max(
										20,
										resizeInfo.current.originalWidth - delta * sign
									);
									newHeight = newWidth;
									newX =
										resizeInfo.current.originalX +
										(resizeInfo.current.originalWidth - newWidth);
									break;
								case 'ne':
									newWidth = Math.max(
										20,
										resizeInfo.current.originalWidth + delta * sign
									);
									newHeight = newWidth;
									newY =
										resizeInfo.current.originalY +
										(resizeInfo.current.originalHeight - newHeight);
									break;
								case 'nw':
									newWidth = Math.max(
										20,
										resizeInfo.current.originalWidth - delta * sign
									);
									newHeight = newWidth;
									newX =
										resizeInfo.current.originalX +
										(resizeInfo.current.originalWidth - newWidth);
									newY =
										resizeInfo.current.originalY +
										(resizeInfo.current.originalHeight - newHeight);
									break;
							}

							return {
								...el,
								width: newWidth,
								height: newHeight,
								x: newX,
								y: newY
							};
						}

						// General resizing logic for other elements
						let newWidth = resizeInfo.current.originalWidth;
						let newHeight = resizeInfo.current.originalHeight;
						let newX = resizeInfo.current.originalX;
						let newY = resizeInfo.current.originalY;

						switch (resizeInfo.current.handle) {
							case 'se':
								newWidth = Math.max(20, resizeInfo.current.originalWidth + dx);
								newHeight = Math.max(20, resizeInfo.current.originalHeight + dy);
								break;
							case 'sw':
								newWidth = Math.max(20, resizeInfo.current.originalWidth - dx);
								newHeight = Math.max(20, resizeInfo.current.originalHeight + dy);
								newX =
									resizeInfo.current.originalX +
									(resizeInfo.current.originalWidth - newWidth);
								break;
							case 'ne':
								newWidth = Math.max(20, resizeInfo.current.originalWidth + dx);
								newHeight = Math.max(20, resizeInfo.current.originalHeight - dy);
								newY =
									resizeInfo.current.originalY +
									(resizeInfo.current.originalHeight - newHeight);
								break;
							case 'nw':
								newWidth = Math.max(20, resizeInfo.current.originalWidth - dx);
								newHeight = Math.max(20, resizeInfo.current.originalHeight - dy);
								newX =
									resizeInfo.current.originalX +
									(resizeInfo.current.originalWidth - newWidth);
								newY =
									resizeInfo.current.originalY +
									(resizeInfo.current.originalHeight - newHeight);
								break;
						}

						return {
							...el,
							width: newWidth,
							height: newHeight,
							x: newX,
							y: newY
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const deleteElement = (id, e) => {
		e.stopPropagation();
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.filter((el) => el.id !== id)
			};
			return newPages;
		});
		setSelectedId(null);
	};

	useEffect(() => {
		const handleGlobalMouseMove = (e) => {
			if (draggedId) handleMouseMove(e);
			if (resizing) handleResize(e);
		};

		const handleGlobalMouseUp = () => {
			if (draggedId) handleMouseUp();
			if (resizing) {
				setResizing(null);
				resizeInfo.current = {
					handle: null,
					startX: 0,
					startY: 0,
					originalWidth: 0,
					originalHeight: 0,
					originalX: 0,
					originalY: 0
				};
			}
		};

		const handleKeyDown = (e) => {
			if (e.key === 'Delete' && selectedId) {
				setPages((prevPages) => {
					const newPages = [...prevPages];
					newPages[currentPage] = {
						...newPages[currentPage],
						elements: newPages[currentPage].elements.filter(
							(el) => el.id !== selectedId
						)
					};
					return newPages;
				});
				setSelectedId(null);
			}
		};

		document.addEventListener('mousemove', handleGlobalMouseMove);
		document.addEventListener('mouseup', handleGlobalMouseUp);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('mousemove', handleGlobalMouseMove);
			document.removeEventListener('mouseup', handleGlobalMouseUp);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [draggedId, resizing, selectedId, currentPage]);

	const addElement = (type) => {
		let element = {
			id: Date.now().toString(),
			type,
			x: 100 + Math.random() * 200,
			y: 100 + Math.random() * 200,
			width: 300, // Default width for text elements
			height: 100, // Default height for text elements
			style: {
				fontFamily: 'Arial',
				fontSize: 12,
				fontWeight: 'normal',
				fontStyle: 'normal',
				textDecoration: 'none',
				textAlign: 'left',
				backgroundColor: 'transparent',
				color: '#000000'
			}
		};

		switch (type) {
			case 'qrCode':
				element = {
					...element,
					width: 90,
					height: 90,
					style: {
						...element.style,
						qrColor: '#000000',
						backgroundColor: '#FFFFFF'
					}
				};
				break;
			case 'address':
				element = {
					...element,
					text: '1234 Main Street\nVancouver, V5P 2H3',
					width: 140,
					height: 70,
					style: {
						...element.style,
						fontSize: 12,
						whiteSpace: 'pre-wrap',
						overflowWrap: 'break-word',
						secondLineSpace: 35,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start'
					}
				};
				break;
			case 'date':
				element = {
					...element,
					type: 'text',
					width: 100,
					height: 30,
					text: '2020-05-03',
					style: {
						...element.style,
						fontSize: 12
					}
				};
				break;
			case 'letterIntro':
				element = {
					...element,
					text: 'I am writing to you regarding your property at ((address)).',
					style: {
						...element.style,
						fontSize: 12
					}
				};
				break;
			case 'letterClosing':
				element = {
					...element,
					text: 'Sincerely,\n((username))',
					style: {
						...element.style,
						fontSize: 12
					}
				};
				break;
			case 'cma':
				element = {
					...element,
					text: 'Recent Activity in Your Area\nSold Homes:\nA1: 2770 Euclid Ave — Sold Dec 2024 for $2,265,000 (Assessed: $2,212,700)\nA2: 3872 Nanaimo St — Sold Dec 2024 for $1,800,000\nA3: 2872 E 25th Ave — Sold Nov 2024 for $1,500,000 (Assessed: $1,548,500)\nActive Listings:\nS1: 2669 Vanness Ave — Listed at $2,600,000 (Assessed: $2,971,000)\nS2: 2780 E 26th Ave — Listed at $2,333,000',
					width: 500,
					height: 230,
					style: {
						...element.style,
						fontSize: 12,
						fontFamily: 'Arial',
						color: '#000000',
						backgroundColor: 'transparent',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'break-word',
						lineHeight: '1.5'
					}
				};
				break;
			case 'dynamicText':
				element = {
					...element,
					type: 'text', // Set as regular text element but with dynamic type
					text: 'Double-click to edit',
					dynamicType: null, // Will be set when user selects a type
					style: {
						...element.style,
						fontSize: 12
					}
				};
				break;
			case 'map':
				element = {
					...element,
					width: 300,
					height: 225,
					activePins: 3,
					soldPins: 2,
					style: {
						backgroundColor: '#e0e0e0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexDirection: 'column'
					}
				};
				break;
			case 'marketStat':
				element = {
					...element,
					width: 200,
					height: 120,
					style: {
						backgroundColor: '#FFFFFF',
						color: '#000000',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexDirection: 'column',
						border: '1px solid #e0e0e0'
					}
				};
				break;
			case 'plexCount':
				element = {
					...element,
					type: 'text',
					width: 120,
					height: 30,
					text: '6 plex',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold'
					}
				};
				break;
			case 'squareFootage':
				element = {
					...element,
					type: 'text',
					width: 150,
					height: 30,
					text: '7,380 sq feet',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold'
					}
				};
				break;
			case 'constructionCosts':
				element = {
					...element,
					type: 'text',
					width: 200,
					height: 30,
					text: '$3,690,000',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold',
						color: '#d32f2f'
					}
				};
				break;
			case 'propertyValue':
				element = {
					...element,
					type: 'text',
					width: 180,
					height: 30,
					text: '$2,600,000',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold',
						color: '#1976d2'
					}
				};
				break;
			case 'mortgageInterest':
				element = {
					...element,
					type: 'text',
					width: 180,
					height: 30,
					text: '$340,457',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold',
						color: '#d32f2f'
					}
				};
				break;
			case 'revenueProjection':
				element = {
					...element,
					type: 'text',
					width: 220,
					height: 30,
					text: '$8,769,285',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold',
						color: '#2e7d32'
					}
				};
				break;
			case 'profitCalculation':
				element = {
					...element,
					type: 'text',
					width: 200,
					height: 30,
					text: '$4,738,828',
					style: {
						...element.style,
						fontSize: 12,
						fontWeight: 'bold',
						color: '#2e7d32'
					}
				};
				break;
			default:
				element.text = type === 'text' ? 'Double-click to edit text' : getDefaultText(type);
		}

		// Update the current page's elements
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
	};

	const calculateImageDimensions = (file) => {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				const originalWidth = img.width;
				const originalHeight = img.height;
				let newWidth = 240;
				let newHeight = 320;

				if (originalWidth / originalHeight > 3 / 4) {
					newWidth = 240;
					newHeight = (240 * originalHeight) / originalWidth;
				} else {
					newHeight = 320;
					newWidth = (320 * originalWidth) / originalHeight;
				}

				resolve({ width: newWidth, height: newHeight });
			};
			img.src = URL.createObjectURL(file);
		});
	};

	const handleImageInputChange = async (e) => {
		const file = e.target.files[0];
		if (file && file.type.startsWith('image/')) {
			await handleImageUpload(file);
			e.target.value = '';
		}
	};

	const addImage = (file, dimensions) => {
		const element = {
			id: Date.now().toString(),
			type: 'image',
			x: 100 + Math.random() * 200,
			y: 100 + Math.random() * 200,
			width: dimensions.width,
			height: dimensions.height,
			src: file,
			portrait: 'rounded' // Default portrait style
		};
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
	};

	const handleImageUpload = async (file) => {
		const dimensions = await calculateImageDimensions(file);
		const reader = new FileReader();

		reader.onload = (event) => {
			addImage(event.target.result, dimensions);
		};
		reader.readAsDataURL(file);
	};

	const handlePortraitChange = (elementId, portrait) => {
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							portrait
						};
					}
					return el;
				})
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							portrait
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const addLine = (type) => {
		const element = {
			id: Date.now().toString(),
			type: 'line',
			x: 100 + Math.random() * 200,
			y: 100 + Math.random() * 200,
			width: type === 'horizontal' ? 200 : 2, // default width
			height: type === 'vertical' ? 200 : 2, // default height
			orientation: type // 'horizontal' or 'vertical'
		};

		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
	};

	const handleStyleChange = (elementId, styleType, value) => {
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							style: {
								...el.style,
								[styleType]: value
							}
						};
					}
					return el;
				})
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							style: {
								...el.style,
								[styleType]: value
							}
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const addIcon = () => {
		const element = {
			id: Date.now().toString(),
			type: 'icon',
			x: 100 + Math.random() * 200,
			y: 100 + Math.random() * 200,
			iconType: 'phone' // Default icon type
		};

		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: [...newPages[currentPage].elements, element]
			};
			return newPages;
		});
	};

	const handleDynamicTypeChange = (elementId, newType) => {
		setPages((prevPages) => {
			const newPages = [...prevPages];
			const currentPageElements = newPages[currentPage].elements;
			const element = currentPageElements.find((el) => el.id === elementId);
			if (!element) return newPages;

			const typeConfig = dynamicTextTypes.find((t) => t.type === newType);
			// If there's existing text, insert the placeholder at the cursor position
			// or at the end if no cursor position is set
			const existingText = element.text === 'New Element' ? '' : element.text;

			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							dynamicType: newType,
							text: existingText + typeConfig.placeholder
						};
					}
					return el;
				})
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			const currentPageElements = newPages[currentPage].elements;
			const element = currentPageElements.find((el) => el.id === elementId);
			if (!element) return newPages;

			const typeConfig = dynamicTextTypes.find((t) => t.type === newType);
			// If there's existing text, insert the placeholder at the cursor position
			// or at the end if no cursor position is set
			const existingText = element.text === 'New Element' ? '' : element.text;

			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							dynamicType: newType,
							text: existingText + typeConfig.placeholder
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const handleDynamicTextClick = (event, elementId) => {
		setDynamicTextAnchorEl(event.currentTarget);
		setSelectedDynamicElementId(elementId);
	};

	const handleDynamicTextClose = () => {
		setDynamicTextAnchorEl(null);
		setSelectedDynamicElementId(null);
	};

	const handleDynamicTypeSelect = (type) => {
		if (selectedDynamicElementId) {
			handleDynamicTypeChange(selectedDynamicElementId, type);
		}
		handleDynamicTextClose();
	};

	const handleMapPinChange = (elementId, pinType, value) => {
		const newValue = Math.max(0, Math.min(10, parseInt(value, 10) || 0)); // Clamp between 0-10
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) =>
					el.id === elementId ? { ...el, [pinType]: newValue } : el
				)
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) =>
					el.id === elementId ? { ...el, [pinType]: newValue } : el
				)
			};
			return newPages;
		});
	};

	const renderElement = (element) => {
		const isSelected = selectedId === element.id;
		const isDragging = draggedId === element.id;
		const elementClasses = `${classes.element} ${classes[element.type]} ${
			isSelected ? classes.selected : ''
		} ${isDragging ? classes.dragging : ''}`;

		const elementStyle = {
			...element.style,
			position: 'absolute',
			left: `${element.x}px`,
			top: `${element.y}px`,
			width: element.width ? `${element.width}px` : 'auto',
			height: element.height ? `${element.height}px` : 'auto',
			zIndex: isSelected ? 5 : 4,
			pointerEvents: 'auto',
			boxShadow: isSelected ? '0 0 0 2px #0096FF' : 'none',
			resize: 'none'
		};

		if (element.type === 'qrCode') {
			return (
				<div
					key={element.id}
					style={{
						...elementStyle,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: element.style?.backgroundColor || '#FFFFFF',
						padding: '2px',
						borderRadius: '0px', // Make outer border square
						position: 'relative'
					}}
					onClick={(e) => {
						e.stopPropagation();
						handleSelect(element.id, e);
					}}
					onMouseDown={(e) => {
						e.stopPropagation();
						if (editingTextId === element.id || resizing) return;
						handleMouseDown(e, element.id);
					}}
				>
					{isSelected && (
						<div className={classes.deleteButton}>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									deleteElement(element.id, e);
								}}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</div>
					)}
					{/* QR Code Visual Representation */}
					<div
						style={{
							width: '90%',
							height: '90%',
							margin: 'auto',
							display: 'grid',
							gridTemplateColumns: 'repeat(7, 1fr)',
							gridTemplateRows: 'repeat(7, 1fr)',
							gap: '2.5px',
							backgroundColor: element.style?.backgroundColor || '#FFFFFF',
							borderRadius: '0px' // Make inner grid square
						}}
					>
						{/* Position Detection Patterns */}
						<div
							style={{
								gridArea: '1 / 1 / 4 / 4',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								gridTemplateRows: 'repeat(3, 1fr)',
								gap: '1px',
								borderRadius: '0px' // Make position pattern square
							}}
						>
							<div
								style={{
									gridArea: '1 / 1 / 4 / 4',
									backgroundColor: element.style?.qrColor || '#000',
									borderRadius: '0px' // Make square
								}}
							></div>
							<div
								style={{
									gridArea: '2 / 2 / 3 / 3',
									backgroundColor: element.style?.backgroundColor || '#FFFFFF',
									borderRadius: '0px' // Make square
								}}
							></div>
						</div>
						<div
							style={{
								gridArea: '1 / 5 / 4 / 8',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								gridTemplateRows: 'repeat(3, 1fr)',
								gap: '1px',
								borderRadius: '0px'
							}}
						>
							<div
								style={{
									gridArea: '1 / 1 / 4 / 4',
									backgroundColor: element.style?.qrColor || '#000',
									borderRadius: '0px'
								}}
							></div>
							<div
								style={{
									gridArea: '2 / 2 / 3 / 3',
									backgroundColor: element.style?.backgroundColor || '#FFFFFF',
									borderRadius: '0px'
								}}
							></div>
						</div>
						<div
							style={{
								gridArea: '5 / 1 / 8 / 4',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								gridTemplateRows: 'repeat(3, 1fr)',
								gap: '1px',
								borderRadius: '0px'
							}}
						>
							<div
								style={{
									gridArea: '1 / 1 / 4 / 4',
									backgroundColor: element.style?.qrColor || '#000',
									borderRadius: '0px'
								}}
							></div>
							<div
								style={{
									gridArea: '2 / 2 / 3 / 3',
									backgroundColor: element.style?.backgroundColor || '#FFFFFF',
									borderRadius: '0px'
								}}
							></div>
						</div>
						{/* Static Data Pattern */}
						{[...Array(21)].map((_, i) => (
							<div
								key={i}
								style={{
									backgroundColor:
										i % 2 === 0
											? element.style?.qrColor || '#000'
											: element.style?.backgroundColor || '#FFFFFF',
									borderRadius: '0px' // Make all static pattern squares
								}}
							/>
						))}
					</div>
					{isSelected && (
						<>
							{['nw', 'ne', 'sw', 'se'].map((handle) => (
								<div
									key={handle}
									className={`${classes.resizeHandle} ${
										classes[`resize-${handle}`]
									}`}
									style={{
										position: 'absolute',
										[handle.includes('n') ? 'top' : 'bottom']: '-6px',
										[handle.includes('w') ? 'left' : 'right']: '-6px',
										cursor: `${handle}-resize`
									}}
									onMouseDown={(e) => handleResizeStart(e, element, handle)}
								/>
							))}
						</>
					)}
					{isSelected && (
						<div
							className={classes.textFormatToolbar}
							ref={formattingToolbarRef}
							onClick={(e) => e.stopPropagation()}
							style={{
								position: 'absolute',
								top: '-45px',
								left: '50%',
								transform: 'translateX(-50%)',
								backgroundColor: 'white',
								padding: '5px',
								borderRadius: '4px',
								boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
								display: 'flex',
								alignItems: 'center',
								gap: '5px',
								zIndex: 1002
							}}
						>
							<Tooltip title="QR Code Color">
								<IconButton
									className={classes.formatButton}
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setShowQrFgColorPicker((v) => !v);
									}}
									style={{
										border: '1px solid #000',
										borderRadius: '4px',
										padding: '4px',
										minWidth: '32px',
										height: '32px'
									}}
								>
									<span
										style={{
											color: element.style?.qrColor || '#000000',
											fontWeight: 'bold'
										}}
									>
										FG
									</span>
								</IconButton>
							</Tooltip>
							{showQrFgColorPicker && (
								<div
									style={{
										position: 'absolute',
										zIndex: 2000,
										left: '100%',
										marginLeft: '10px',
										top: '50%',
										transform: 'translateY(-50%)'
									}}
									onMouseDown={(e) => e.stopPropagation()}
									onClick={(e) => e.stopPropagation()}
								>
									<SketchPicker
										color={element.style?.qrColor || '#000000'}
										onChange={(color) =>
											handleStyleChange(selectedId, 'qrColor', color.hex)
										}
									/>
								</div>
							)}
							<Tooltip title="Background Color">
								<IconButton
									className={classes.formatButton}
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setShowQrBgColorPicker((v) => !v);
									}}
									style={{
										border: '1px solid #000',
										borderRadius: '4px',
										padding: '4px',
										minWidth: '32px',
										height: '32px'
									}}
								>
									<span
										style={{
											color: element.style?.backgroundColor || '#FFFFFF',
											fontWeight: 'bold'
										}}
									>
										BG
									</span>
								</IconButton>
							</Tooltip>
							{showQrBgColorPicker && (
								<div
									style={{
										position: 'absolute',
										zIndex: 2000,
										left: '100%',
										marginLeft: '10px',
										top: '50%',
										transform: 'translateY(-50%)'
									}}
									onMouseDown={(e) => e.stopPropagation()}
									onClick={(e) => e.stopPropagation()}
								>
									<SketchPicker
										color={element.style?.backgroundColor || '#FFFFFF'}
										onChange={(color) =>
											handleStyleChange(
												selectedId,
												'backgroundColor',
												color.hex
											)
										}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			);
		}

		if (element.type === 'line') {
			return (
				<div
					key={element.id}
					style={{
						...elementStyle,
						backgroundColor: element.style?.color || '#000000',
						width: element.orientation === 'horizontal' ? `${element.width}px` : '2px',
						height: element.orientation === 'vertical' ? `${element.height}px` : '2px',
						cursor: 'grab'
					}}
					onClick={(e) => handleSelect(element.id, e)}
					onMouseDown={(e) => {
						if (editingTextId === element.id || resizing) return;
						handleMouseDown(e, element.id);
					}}
				>
					{isSelected && (
						<>
							<div className={classes.deleteButton}>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										deleteElement(element.id, e);
									}}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
							{/* Resize handles for lines */}
							{element.orientation === 'horizontal' ? (
								<>
									<div
										className={`${classes.lineResizeHandle} ${classes['line-start']}`}
										style={{
											left: '-5px',
											top: '50%',
											transform: 'translateY(-50%)'
										}}
										onMouseDown={(e) =>
											handleLineResizeStart(e, element, 'start')
										}
									/>
									<div
										className={`${classes.lineResizeHandle} ${classes['line-end']}`}
										style={{
											right: '-5px',
											top: '50%',
											transform: 'translateY(-50%)'
										}}
										onMouseDown={(e) =>
											handleLineResizeStart(e, element, 'end')
										}
									/>
								</>
							) : (
								<>
									<div
										className={`${classes.lineResizeHandle} ${classes['line-start']}`}
										style={{
											top: '-5px',
											left: '50%',
											transform: 'translateX(-50%)'
										}}
										onMouseDown={(e) =>
											handleLineResizeStart(e, element, 'start')
										}
									/>
									<div
										className={`${classes.lineResizeHandle} ${classes['line-end']}`}
										style={{
											bottom: '-5px',
											left: '50%',
											transform: 'translateX(-50%)'
										}}
										onMouseDown={(e) =>
											handleLineResizeStart(e, element, 'end')
										}
									/>
								</>
							)}
						</>
					)}
				</div>
			);
		} else if (element.type === 'map') {
			return (
				<div
					key={element.id}
					className={elementClasses}
					style={{
						...elementStyle,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center'
					}}
					data-element-id={element.id}
					onClick={(e) => handleSelect(element.id, e)}
					onMouseDown={(e) => {
						if (resizing) return;
						handleMouseDown(e, element.id);
					}}
				>
					{isSelected && (
						<div className={classes.deleteButton}>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									deleteElement(element.id, e);
								}}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</div>
					)}
					<MapIcon
						style={{
							fontSize: Math.min(element.width, element.height) * 0.3 || 48,
							color: '#757575'
						}}
					/>
					<Typography
						variant="caption"
						style={{ color: '#757575', marginTop: '8px', textAlign: 'center' }}
					>
						Map Area
					</Typography>
					{isSelected && (
						<div
							style={{
								position: 'absolute',
								bottom: '5px',
								left: '5px',
								right: '5px',
								backgroundColor: 'rgba(255,255,255,0.95)',
								padding: '8px',
								borderRadius: '4px',
								zIndex: 1005,
								display: 'flex',
								gap: '8px',
								pointerEvents: 'auto',
								boxShadow: '0px 1px 3px rgba(0,0,0,0.2)'
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<TextField
								label="Active Pins"
								type="number"
								size="small"
								variant="outlined"
								value={element.activePins === undefined ? 3 : element.activePins}
								onChange={(e) =>
									handleMapPinChange(element.id, 'activePins', e.target.value)
								}
								style={{ width: '50%' }}
								inputProps={{
									min: 0,
									max: 10,
									style: { fontSize: '0.8rem', padding: '8px' }
								}}
								InputLabelProps={{ style: { fontSize: '0.8rem' } }}
							/>
							<TextField
								label="Sold Pins"
								type="number"
								size="small"
								variant="outlined"
								value={element.soldPins === undefined ? 2 : element.soldPins}
								onChange={(e) =>
									handleMapPinChange(element.id, 'soldPins', e.target.value)
								}
								style={{ width: '50%' }}
								inputProps={{
									min: 0,
									max: 10,
									style: { fontSize: '0.8rem', padding: '8px' }
								}}
								InputLabelProps={{ style: { fontSize: '0.8rem' } }}
							/>
						</div>
					)}
					{isSelected && (
						<>
							{['nw', 'ne', 'sw', 'se'].map((handle) => (
								<div
									key={handle}
									className={`${classes.resizeHandle} ${
										classes[`resize-${handle}`]
									}`}
									style={{
										position: 'absolute',
										[handle.includes('n') ? 'top' : 'bottom']: '-6px',
										[handle.includes('w') ? 'left' : 'right']: '-6px',
										cursor: `${handle}-resize`
									}}
									onMouseDown={(e) => handleResizeStart(e, element, handle)}
								/>
							))}
						</>
					)}
				</div>
			);
		} else if (element.type === 'marketStat') {
			return (
				<div
					key={element.id}
					className={elementClasses}
					style={{
						...elementStyle,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						border: '1px solid #e0e0e0',
						aspectRatio: '848/658'
					}}
					data-element-id={element.id}
					onClick={(e) => handleSelect(element.id, e)}
					onMouseDown={(e) => {
						if (resizing) return;
						handleMouseDown(e, element.id);
					}}
				>
					{isSelected && (
						<div className={classes.deleteButton}>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									deleteElement(element.id, e);
								}}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</div>
					)}
					<BarChartIcon
						style={{
							fontSize: Math.min(element.width, element.height) * 0.3 || 48,
							color: '#757575'
						}}
					/>
					<Typography
						variant="caption"
						style={{ color: '#757575', marginTop: '8px', textAlign: 'center' }}
					>
						Market Statistics
					</Typography>
					{isSelected && (
						<>
							{['nw', 'ne', 'sw', 'se'].map((handle) => (
								<div
									key={handle}
									className={`${classes.resizeHandle} ${
										classes[`resize-${handle}`]
									}`}
									style={{
										position: 'absolute',
										[handle.includes('n') ? 'top' : 'bottom']: '-6px',
										[handle.includes('w') ? 'left' : 'right']: '-6px',
										cursor: `${handle}-resize`
									}}
									onMouseDown={(e) => handleResizeStart(e, element, handle)}
								/>
							))}
						</>
					)}
				</div>
			);
		}

		return (
			<div
				style={elementStyle}
				data-element-id={element.id}
				onClick={(e) => handleSelect(element.id, e)}
				onMouseDown={(e) => {
					if (editingTextId === element.id || resizing) return;
					handleMouseDown(e, element.id);
				}}
			>
				{isSelected && (
					<div className={classes.deleteButton}>
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								deleteElement(element.id, e);
							}}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</div>
				)}
				{isSelected &&
					(element.type === 'text' || element.type === 'cma') &&
					!editingTextId && (
						<div
							className={classes.textFormatToolbar}
							ref={formattingToolbarRef}
							onClick={(e) => e.stopPropagation()}
						>
							<Tooltip title="Font Family">
								<Select
									className={classes.fontSelect}
									value={element.style?.fontFamily || 'Arial'}
									onChange={(e) => applyTextStyle('fontFamily', e.target.value)}
									size="small"
								>
									{fontOptions.map((font) => (
										<MenuItem
											key={font}
											value={font}
											style={{ fontFamily: font }}
										>
											{font}
										</MenuItem>
									))}
								</Select>
							</Tooltip>
							<Tooltip title="Font Size">
								<Select
									className={classes.sizeSelect}
									value={parseInt(element.style?.fontSize) || 16}
									onChange={(e) =>
										applyTextStyle('fontSize', `${e.target.value}px`)
									}
									size="small"
								>
									{fontSizes.map((size) => (
										<MenuItem key={size} value={size}>
											{size}
										</MenuItem>
									))}
								</Select>
							</Tooltip>
							<Tooltip title="Bold">
								<IconButton
									className={`${classes.formatButton} ${
										element.style?.fontWeight === 'bold' ? 'active' : ''
									}`}
									size="small"
									onClick={() =>
										applyTextStyle(
											'fontWeight',
											element.style?.fontWeight === 'bold' ? 'normal' : 'bold'
										)
									}
								>
									<FormatBoldIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Italic">
								<IconButton
									className={`${classes.formatButton} ${
										element.style?.fontStyle === 'italic' ? 'active' : ''
									}`}
									size="small"
									onClick={() =>
										applyTextStyle(
											'fontStyle',
											element.style?.fontStyle === 'italic'
												? 'normal'
												: 'italic'
										)
									}
								>
									<FormatItalicIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Underline">
								<IconButton
									className={`${classes.formatButton} ${
										element.style?.textDecoration === 'underline'
											? 'active'
											: ''
									}`}
									size="small"
									onClick={() =>
										applyTextStyle(
											'textDecoration',
											element.style?.textDecoration === 'underline'
												? 'none'
												: 'underline'
										)
									}
								>
									<FormatUnderlinedIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Text Color">
								<IconButton
									className={classes.formatButton}
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setShowTextColorPicker((v) => !v);
									}}
								>
									<span style={{ color: element.style?.color || '#000000' }}>
										A
									</span>
								</IconButton>
							</Tooltip>
							{showTextColorPicker && (
								<div
									style={{
										position: 'absolute',
										zIndex: 2000,
										marginTop: 40,
										left: 0
									}}
									onMouseDown={(e) => e.stopPropagation()}
									onClick={(e) => e.stopPropagation()}
								>
									<SketchPicker
										color={element.style?.color || '#000000'}
										onChange={(color) =>
											handleStyleChange(selectedId, 'color', color.hex)
										}
									/>
								</div>
							)}
							{!element.dynamicType && (
								<Tooltip title="Add Dynamic Text">
									<IconButton
										className={classes.formatButton}
										size="small"
										onClick={(e) => handleDynamicTextClick(e, element.id)}
									>
										<TodayIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
						</div>
					)}
				{isSelected && element.type === 'address' && !editingTextId && (
					<div
						className={classes.textFormatToolbar}
						ref={formattingToolbarRef}
						onClick={(e) => e.stopPropagation()}
					>
						<Tooltip title="Font Family">
							<Select
								className={classes.fontSelect}
								value={element.style?.fontFamily || 'Arial'}
								onChange={(e) => applyTextStyle('fontFamily', e.target.value)}
								size="small"
							>
								{fontOptions.map((font) => (
									<MenuItem key={font} value={font} style={{ fontFamily: font }}>
										{font}
									</MenuItem>
								))}
							</Select>
						</Tooltip>
						<Tooltip title="Font Size">
							<Select
								className={classes.sizeSelect}
								value={parseInt(element.style?.fontSize) || 16}
								onChange={(e) => applyTextStyle('fontSize', `${e.target.value}px`)}
								size="small"
							>
								{fontSizes.map((size) => (
									<MenuItem key={size} value={size}>
										{size}
									</MenuItem>
								))}
							</Select>
						</Tooltip>
						<Tooltip title="Line Spacing">
							<TextField
								label="Line Spacing"
								type="number"
								size="small"
								variant="outlined"
								value={element.style?.secondLineSpace || 35}
								onChange={(e) => {
									const value = Math.max(15, parseInt(e.target.value) || 35);
									handleStyleChange(element.id, 'secondLineSpace', value);
								}}
								style={{ width: '100px' }}
								inputProps={{
									min: 15,
									max: 100,
									style: { fontSize: '0.8rem', padding: '8px' }
								}}
								InputLabelProps={{ style: { fontSize: '0.8rem' } }}
							/>
						</Tooltip>
						<Tooltip title="Bold">
							<IconButton
								className={`${classes.formatButton} ${
									element.style?.fontWeight === 'bold' ? 'active' : ''
								}`}
								size="small"
								onClick={() =>
									applyTextStyle(
										'fontWeight',
										element.style?.fontWeight === 'bold' ? 'normal' : 'bold'
									)
								}
							>
								<FormatBoldIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Italic">
							<IconButton
								className={`${classes.formatButton} ${
									element.style?.fontStyle === 'italic' ? 'active' : ''
								}`}
								size="small"
								onClick={() =>
									applyTextStyle(
										'fontStyle',
										element.style?.fontStyle === 'italic' ? 'normal' : 'italic'
									)
								}
							>
								<FormatItalicIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Underline">
							<IconButton
								className={`${classes.formatButton} ${
									element.style?.textDecoration === 'underline' ? 'active' : ''
								}`}
								size="small"
								onClick={() =>
									applyTextStyle(
										'textDecoration',
										element.style?.textDecoration === 'underline'
											? 'none'
											: 'underline'
									)
								}
							>
								<FormatUnderlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Text Color">
							<IconButton
								className={classes.formatButton}
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setShowTextColorPicker((v) => !v);
								}}
							>
								<span style={{ color: element.style?.color || '#000000' }}>A</span>
							</IconButton>
						</Tooltip>
						{showTextColorPicker && (
							<div
								style={{
									position: 'absolute',
									zIndex: 2000,
									marginTop: 40,
									left: 0
								}}
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								<SketchPicker
									color={element.style?.color || '#000000'}
									onChange={(color) =>
										handleStyleChange(selectedId, 'color', color.hex)
									}
								/>
							</div>
						)}
					</div>
				)}
				{isSelected && element.type === 'qrCode' && (
					<div
						className={classes.textFormatToolbar}
						ref={formattingToolbarRef}
						onClick={(e) => e.stopPropagation()}
					>
						<Tooltip title="QR Code Color">
							<IconButton
								className={classes.formatButton}
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setShowQrFgColorPicker((v) => !v);
								}}
							>
								<span style={{ color: element.style?.qrColor || '#000000' }}>
									QR
								</span>
							</IconButton>
						</Tooltip>
						{showQrFgColorPicker && (
							<div
								style={{
									position: 'absolute',
									zIndex: 2000,
									marginTop: 40,
									left: 0
								}}
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								<SketchPicker
									color={element.style?.qrColor || '#000000'}
									onChange={(color) =>
										handleStyleChange(selectedId, 'qrColor', color.hex)
									}
								/>
							</div>
						)}
						<Tooltip title="Background Color">
							<IconButton
								className={classes.formatButton}
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setShowQrBgColorPicker((v) => !v);
								}}
							>
								<span
									style={{ color: element.style?.backgroundColor || '#FFFFFF' }}
								>
									BG
								</span>
							</IconButton>
						</Tooltip>
						{showQrBgColorPicker && (
							<div
								style={{
									position: 'absolute',
									zIndex: 2000,
									marginTop: 40,
									left: 0
								}}
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								<SketchPicker
									color={element.style?.backgroundColor || '#FFFFFF'}
									onChange={(color) =>
										handleStyleChange(selectedId, 'backgroundColor', color.hex)
									}
								/>
							</div>
						)}
					</div>
				)}
				{(element.type === 'text' ||
					element.type === 'letterIntro' ||
					element.type === 'letterClosing' ||
					element.type === 'address' ||
					element.type === 'cma') &&
					(editingTextId === element.id ? (
						<textarea
							data-id={element.id}
							value={element.text}
							onChange={(e) => handleTextChange(e, element.id)}
							onBlur={handleTextBlur}
							autoFocus
							style={{
								...element.style,
								width: '100%',
								height: '100%',
								border: 'none',
								padding: '10px',
								resize: 'none',
								backgroundColor: 'transparent',
								outline: 'none',
								cursor: 'text',
								whiteSpace: 'pre-wrap',
								overflowWrap: 'break-word'
							}}
						/>
					) : (
						<Typography
							variant="body1"
							onDoubleClick={(e) => handleDoubleClick(element, e)}
							style={{
								...element.style,
								width: '100%',
								height: '100%',
								cursor: 'text',
								padding: '10px',
								whiteSpace: 'pre-wrap',
								overflowWrap: 'break-word',
								...(element.type === 'address' && {
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
									'& > div': {
										lineHeight: '1.2'
									}
								}),
								...(element.type === 'cma' && { lineHeight: 1.74 })
							}}
						>
							{element.type === 'address' ? (
								<>
									<div>{element.text.split('\n')[0]}</div>
									<div
										style={{
											marginTop: `${Math.max(
												-35,
												(element.style?.secondLineSpace || 35) - 43
											)}px`,
											lineHeight: '5.2'
										}}
									>
										{element.text.split('\n')[1]}
									</div>
								</>
							) : (
								element.text
							)}
						</Typography>
					))}
				{isSelected && (
					<>
						{['nw', 'ne', 'sw', 'se'].map((handle) => (
							<div
								key={handle}
								className={`${classes.resizeHandle} ${classes[`resize-${handle}`]}`}
								style={{
									position: 'absolute',
									[handle.includes('n') ? 'top' : 'bottom']: '-6px',
									[handle.includes('w') ? 'left' : 'right']: '-6px',
									cursor: `${handle}-resize`
								}}
								onMouseDown={(e) => handleResizeStart(e, element, handle)}
							/>
						))}
					</>
				)}
			</div>
		);
	};

	const handleLineResizeStart = (e, element, handle) => {
		e.stopPropagation();
		e.preventDefault();
		setResizing(element.id);
		resizeInfo.current = {
			handle,
			startX: e.clientX,
			startY: e.clientY,
			originalWidth: element.width,
			originalHeight: element.height,
			originalX: element.x,
			originalY: element.y
		};
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			if (!currentTemplate) {
				setSaveError('No template selected');
				return;
			}

			// Build settings for all pages
			const newSettings = {};

			// Process first page (main page)
			const mainPage = pages[0];
			const mainDims = mainPage.dimensions || { width: 612, height: 792 };
			if (mainPage) {
				// Save address element
				const addressElement = mainPage.elements.find((el) => el.type === 'address');
				if (addressElement) {
					const secondLineSpace = addressElement.style?.secondLineSpace || 35;
					const { x, y } = convertFrontendToReportLab(
						addressElement.x,
						addressElement.y + (secondLineSpace + 20),
						mainDims.height
					);
					newSettings.Address = {
						x_cordinate: x,
						y_cordinate: y,
						font_size: parseInt(addressElement.style?.fontSize) || 12,
						font_size: parseInt(addressElement.style?.fontSize) || 12,
						color: addressElement.style?.color || '#000000',
						second_line_space: addressElement.style?.secondLineSpace || 35
					};
				}

				// Save date element
				const dateElement = mainPage.elements.find((el) => el.text === '2020-05-03');
				if (dateElement) {
					const { x, y } = convertFrontendToReportLab(
						dateElement.x,
						dateElement.y + 20,
						mainDims.height
					);
					newSettings.Date = {
						x_cordinate: x + 1,
						y_cordinate: y,
						font_size: parseInt(dateElement.style?.fontSize) || 12,
						color: dateElement.style?.color || '#000000'
					};
				}

				// Save QR code element
				const qrElement = mainPage.elements.find((el) => el.type === 'qrCode');
				if (qrElement) {
					const { x, y } = convertFrontendToReportLab(
						qrElement.x - 9,
						qrElement.y + qrElement.width, //(mainDims.height === 792 ? 60 : 67),
						mainDims.height
					);
					newSettings.qr_code = {
						x_coordinate: x,
						y_coordinate: y,
						width: convertPixelsToPoints(qrElement.width),
						height: convertPixelsToPoints(qrElement.height),
						colour: qrElement.style?.qrColor || '#000000',
						bg_colour: qrElement.style?.backgroundColor || '#FFFFFF'
					};
				}

				// Save map element
				const mapElement = mainPage.elements.find((el) => el.type === 'map');
				if (mapElement) {
					const { x, y } = convertFrontendToReportLab(
						mapElement.x - 9,
						mapElement.y,
						mainDims.height
					);
					newSettings.Map = {
						x_coordinate: x,
						y_coordinate: y - convertPixelsToPoints(mapElement.height),
						width: convertPixelsToPoints(mapElement.width),
						height: convertPixelsToPoints(mapElement.height),
						number_of_active_properties:
							mapElement.activePins !== undefined ? mapElement.activePins : 3,
						number_of_sold_properties:
							mapElement.soldPins !== undefined ? mapElement.soldPins : 2
					};
				}

				// Save market statistics element
				const marketStatElement = mainPage.elements.find((el) => el.type === 'marketStat');
				if (marketStatElement) {
					const { x, y } = convertFrontendToReportLab(
						marketStatElement.x - 9,
						marketStatElement.y + marketStatElement.height,
						mainDims.height
					);
					newSettings.market_stat = {
						x_coordinate: x,
						y_coordinate: y,
						width: convertPixelsToPoints(marketStatElement.width),
						height: convertPixelsToPoints(marketStatElement.height),
						colour: marketStatElement.style?.color || '#000000',
						bg_colour: marketStatElement.style?.backgroundColor || '#FFFFFF'
					};
				}

				// Save CMA element
				const pageCmaElement = mainPage.elements.find(
					(el) =>
						el.type === 'cma' ||
						(el.text && el.text.startsWith('Recent Activity in Your Area'))
				);
				if (pageCmaElement) {
					const { x, y } = convertFrontendToReportLab(
						pageCmaElement.x - 20,
						pageCmaElement.y + 29,
						mainDims.height
					);
					newSettings.CMA = {
						font_size: parseInt(pageCmaElement.style?.fontSize) || 9,
						text_color: pageCmaElement.style?.color || '#212163',
						x_cordinate: x,
						y_cordinate: y,
						width: convertPixelsToPoints(pageCmaElement.width),
						your_neighbourhood_text: pageCmaElement.text
					};
				}

				// Save letter body elements
				const introElement = mainPage.elements.find((el) => el.type === 'letterIntro');
				const closingElement = mainPage.elements.find((el) => el.type === 'letterClosing');
				if (introElement || closingElement) {
					newSettings.LetterBody = {};
					if (introElement) {
						const { x, y } = convertFrontendToReportLab(
							introElement.x,
							introElement.y,
							mainDims.height
						);
						newSettings.LetterBody.LetterNotSoldUsernameIntro = introElement.text;
						newSettings.LetterBody.x_coordinate = x;
						newSettings.LetterBody.y_coordinate = y;
						newSettings.LetterBody.font_size =
							parseInt(introElement.style?.fontSize) || 12;
						newSettings.LetterBody.font_size =
							parseInt(introElement.style?.fontSize) || 12;
					}
					if (closingElement) {
						const { x, y } = convertFrontendToReportLab(
							closingElement.x,
							closingElement.y,
							mainDims.height
						);
						newSettings.LetterBody.LetterUsernameClosing = closingElement.text;
						newSettings.LetterBody.closing_x_coordinate = x;
						newSettings.LetterBody.closing_y_coordinate = y;
						newSettings.LetterBody.closing_font_size =
							parseInt(closingElement.style?.fontSize) || 12;
					}
				}

				// Save text elements
				const textElements = mainPage.elements.filter(
					(el) => el.type === 'text' && el.text !== '2020-05-03'
				);
				if (textElements.length > 0) {
					newSettings.textElements = textElements.map((element) => {
						const { x, y } = convertFrontendToReportLab(
							element.x,
							element.y,
							canvasDimensions.height
						);
						return {
							x_coordinate: x,
							y_coordinate: y,
							width: convertPixelsToPoints(element.width),
							height: convertPixelsToPoints(element.height),
							text: element.text,
							style: {
								fontSize: parseInt(element.style?.fontSize) || 12,
								fontSize: parseInt(element.style?.fontSize) || 12,
								fontFamily: element.style?.fontFamily || 'Arial',
								color: element.style?.color || '#000000',
								fontWeight: element.style?.fontWeight || 'normal',
								fontStyle: element.style?.fontStyle || 'normal',
								textDecoration: element.style?.textDecoration || 'none'
							}
						};
					});
				}

				// Add fullLetterhead to settings with all required fields
				newSettings.fullLetterhead = {
					fileName: mainPage.letterhead?.fileName || null,
					showGreeting: 0,
					showDateOnHeader: mainPage.letterheadSettings.showDateOnHeader ? 1 : 0,
					showAddressSection: 0,
					showYourHomeSection: 0,
					showComparableSection: 0,
					excludeYourHomeInLetter: 0,
					startLetterFromTopPixel:
						mainPage.letterheadSettings.startLetterFromTopPixel?.toString() || '62',
					showYourNeighbourhoodTitle: mainPage.letterheadSettings
						.showYourNeighbourhoodTitle
						? 1
						: 0
				};
				// Add new_scaled: 1 to main page settings
				newSettings.new_scaled = 1;

				// Add blank LetterBody if not present
				if (!newSettings.LetterBody) {
					newSettings.LetterBody = {
						LetterUsernameClosing: '',
						LetterNotSoldUsernameIntro: ''
					};
				}
			}

			// Process additional pages
			for (let i = 1; i < pages.length; i++) {
				const page = pages[i];
				const dims = page.dimensions || { width: 612, height: 792 };
				const pageSettings = {};

				// Save CMA element
				const pageCmaElement = page.elements.find(
					(el) =>
						el.type === 'cma' ||
						(el.text && el.text.startsWith('Recent Activity in Your Area'))
				);
				if (pageCmaElement) {
					const { x, y } = convertFrontendToReportLab(
						pageCmaElement.x - 20,
						pageCmaElement.y + 29,
						dims.height
					);
					pageSettings.CMA = {
						font_size: parseInt(pageCmaElement.style?.fontSize) || 9,
						text_color: pageCmaElement.style?.color || '#212163',
						x_cordinate: x,
						y_cordinate: y,
						width: convertPixelsToPoints(pageCmaElement.width),
						your_neighbourhood_text: pageCmaElement.text
					};
				}

				// Save map element for additional pages
				const pageMapElement = page.elements.find((el) => el.type === 'map');
				if (pageMapElement) {
					const { x, y } = convertFrontendToReportLab(
						pageMapElement.x - 9,
						pageMapElement.y,
						dims.height
					);
					pageSettings.Map = {
						x_coordinate: x,
						y_coordinate: y - convertPixelsToPoints(pageMapElement.height),
						width: convertPixelsToPoints(pageMapElement.width),
						height: convertPixelsToPoints(pageMapElement.height),
						number_of_active_properties:
							pageMapElement.activePins !== undefined ? pageMapElement.activePins : 3,
						number_of_sold_properties:
							pageMapElement.soldPins !== undefined ? pageMapElement.soldPins : 2
					};
				}

				// Save market statistics element for additional pages
				const pageMarketStatElement = page.elements.find((el) => el.type === 'marketStat');
				if (pageMarketStatElement) {
					const { x, y } = convertFrontendToReportLab(
						pageMarketStatElement.x - 9,
						pageMarketStatElement.y + pageMarketStatElement.height,
						dims.height
					);
					pageSettings.market_stat = {
						x_coordinate: x,
						y_coordinate: y,
						width: convertPixelsToPoints(pageMarketStatElement.width),
						height: convertPixelsToPoints(pageMarketStatElement.height),
						colour: pageMarketStatElement.style?.color || '#000000',
						bg_colour: pageMarketStatElement.style?.backgroundColor || '#FFFFFF'
					};
				}

				// Save date element for additional pages
				const pageDateElement = page.elements.find((el) => el.text === '2020-05-03');
				if (pageDateElement) {
					const { x, y } = convertFrontendToReportLab(
						pageDateElement.x,
						pageDateElement.y + 20,
						dims.height
					);
					pageSettings.Date = {
						x_cordinate: x + 1,
						y_cordinate: y,
						font_size: parseInt(pageDateElement.style?.fontSize) || 12,
						y_cordinate: y,
						font_size: parseInt(pageDateElement.style?.fontSize) || 12,
						color: pageDateElement.style?.color || '#000000'
					};
				}

				// save qr code for additional pages
				const pageQrElement = page.elements.find((el) => el.type === 'qrCode');
				if (pageQrElement) {
					const { x, y } = convertFrontendToReportLab(
						pageQrElement.x - 9,
						pageQrElement.y + pageQrElement.width,
						dims.height
					);
					pageSettings.qr_code = {
						x_coordinate: x,
						y_coordinate: y,
						width: convertPixelsToPoints(pageQrElement.width),
						height: convertPixelsToPoints(pageQrElement.height),
						colour: pageQrElement.style?.qrColor || '#000000',
						bg_colour: pageQrElement.style?.backgroundColor || '#FFFFFF'
					};
				}

				// Save address element for additional pages
				const pageAddressElement = page.elements.find((el) => el.type === 'address');
				if (pageAddressElement) {
					const secondLineSpace = pageAddressElement.style?.secondLineSpace || 35;
					const { x, y } = convertFrontendToReportLab(
						pageAddressElement.x,
						pageAddressElement.y + (secondLineSpace + 20),
						dims.height
					);
					pageSettings.Address = {
						font_size: parseInt(pageAddressElement.style?.fontSize) || 6,
						font_size: parseInt(pageAddressElement.style?.fontSize) || 6,
						x_cordinate: x,
						y_cordinate: y,
						color: pageAddressElement.style?.color || '#000000',
						color: pageAddressElement.style?.color || '#000000',
						second_line_space: pageAddressElement.style?.secondLineSpace || 7
					};
				}

				// Save letter body elements for additional pages
				const pageIntroElement = page.elements.find((el) => el.type === 'letterIntro');
				const pageClosingElement = page.elements.find((el) => el.type === 'letterClosing');
				if (pageIntroElement || pageClosingElement) {
					pageSettings.LetterBody = {
						LetterUsernameClosing: pageClosingElement?.text || '',
						LetterNotSoldUsernameIntro: pageIntroElement?.text || ''
					};
				}

				// Add letterhead settings for additional pages
				pageSettings.fullLetterhead = {
					fileName: page.letterhead?.fileName || null,
					showGreeting: 0,
					showDateOnHeader: page.letterheadSettings.showDateOnHeader ? 1 : 0,
					showAddressSection: 0,
					showYourHomeSection: 0,
					showComparableSection: 0,
					excludeYourHomeInLetter: 0,
					startLetterFromTopPixel:
						page.letterheadSettings.startLetterFromTopPixel?.toString() || '98',
					showYourNeighbourhoodTitle: page.letterheadSettings.showYourNeighbourhoodTitle
						? 1
						: 0
				};

				// Add blank LetterBody for additional pages if not present
				if (!pageSettings.LetterBody) {
					pageSettings.LetterBody = {
						LetterUsernameClosing: '',
						LetterNotSoldUsernameIntro: ''
					};
				}
				// Add new_scaled: 1 to each additional page settings
				pageSettings.new_scaled = 1;

				// Add the page settings to the main settings object
				newSettings[`page${i + 1}`] = pageSettings;
			}

			// Save to server
			const templateData = {
				name: currentTemplate.name,
				settings: newSettings
			};

			const response = await axios.put(
				`/api/letter_templates/${currentTemplate.id}`,
				templateData
			);

			if (response.status === 200) {
				setSaveSuccess(true);
				setSaveError(null);
				setCurrentTemplate({
					...currentTemplate,
					settings: newSettings
				});
			}
		} catch (error) {
			console.error('Error saving template:', error);
			setSaveError('Failed to save template. Please try again.');
			setSaveSuccess(false);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setSaveSuccess(false);
		setSaveError(null);
	};

	const handleIconChange = (elementId, iconType) => {
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							iconType
						};
					}
					return el;
				})
			};
			return newPages;
		});
		setPages((prevPages) => {
			const newPages = [...prevPages];
			newPages[currentPage] = {
				...newPages[currentPage],
				elements: newPages[currentPage].elements.map((el) => {
					if (el.id === elementId) {
						return {
							...el,
							iconType
						};
					}
					return el;
				})
			};
			return newPages;
		});
	};

	const handleDimensionChange = (dimension, value) => {
		const numValue = parseInt(value, 10);
		if (!isNaN(numValue)) {
			setTempDimensions((prev) => ({
				...prev,
				[dimension]: numValue
			}));
		}
	};

	const applyDimensions = () => {
		const newDimensions = {
			width: Math.max(tempDimensions.width, 100),
			height: Math.max(tempDimensions.height, 100)
		};
		setCanvasDimensions(newDimensions);
		setTempDimensions(newDimensions);
	};

	const addToHistory = (newElements) => {
		const newHistory = history.slice(0, currentIndex + 1);
		newHistory.push([...newElements]);
		setHistory(newHistory);
		setCurrentIndex(newHistory.length - 1);
	};

	const handleUndo = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: [...history[currentIndex - 1]]
				};
				return newPages;
			});
		}
	};

	const handleRedo = () => {
		if (currentIndex < history.length - 1) {
			setCurrentIndex(currentIndex + 1);
			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: [...history[currentIndex + 1]]
				};
				return newPages;
			});
		}
	};

	useEffect(() => {
		if (pages[currentPage].elements.length > 0 && history.length === 0) {
			addToHistory([...pages[currentPage].elements]);
		}
	}, [pages[currentPage].elements]);

	const applyTextStyle = (styleType, value) => {
		const selection = window.getSelection();
		const currentPageElements = pages[currentPage].elements;
		const element = currentPageElements.find((el) => el.id === selectedId);
		if (!element) return;

		if (selection.rangeCount && selection.toString()) {
			// Apply style to highlighted text
			const range = selection.getRangeAt(0);
			const selectedText = range.toString();

			const newText =
				element.text.slice(0, range.startOffset) +
				`<span style="${styleType}: ${value};">${selectedText}</span>` +
				element.text.slice(range.endOffset);

			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: newPages[currentPage].elements.map((el) =>
						el.id === selectedId ? { ...el, text: newText } : el
					)
				};
				return newPages;
			});
			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: newPages[currentPage].elements.map((el) =>
						el.id === selectedId ? { ...el, text: newText } : el
					)
				};
				return newPages;
			});
		} else {
			// Apply style to the entire text
			const newStyle = { ...element.style, [styleType]: value };
			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: newPages[currentPage].elements.map((el) =>
						el.id === selectedId ? { ...el, style: newStyle } : el
					)
				};
				return newPages;
			});
			setPages((prevPages) => {
				const newPages = [...prevPages];
				newPages[currentPage] = {
					...newPages[currentPage],
					elements: newPages[currentPage].elements.map((el) =>
						el.id === selectedId ? { ...el, style: newStyle } : el
					)
				};
				return newPages;
			});
		}
	};

	const handleLetterheadSelect = async (fileName, pageIndex = 0) => {
		try {
			const response = await axios.get(`/api/letterheads/${fileName}`, {
				responseType: 'arraybuffer',
				headers: { Accept: 'application/pdf' }
			});
			if (response.data) {
				const base64 = btoa(
					new Uint8Array(response.data).reduce(
						(data, byte) => data + String.fromCharCode(byte),
						''
					)
				);
				const dataUrl = `data:application/pdf;base64,${base64}`;
				const dims = await extractPdfPageSize(dataUrl);
				setPages((prevPages) => {
					const newPages = [...prevPages];
					newPages[pageIndex] = {
						...newPages[pageIndex],
						letterhead: {
							url: dataUrl,
							fileName: fileName
						},
						dimensions: dims
					};
					// If this is the current page, update canvas size
					if (pageIndex === currentPage) {
						syncCanvasToCurrentPage(newPages, pageIndex);
					}
					return newPages;
				});
			}
		} catch (error) {
			console.error('Error loading letterhead:', error);
			setUploadError('Failed to load letterhead');
		}
	};

	const onDocumentLoadSuccess = ({ numPages }) => {
		console.log('PDF loaded successfully with', numPages, 'pages');
		setNumPages(numPages);
		setPdfError(null);
	};

	const onDocumentLoadError = (error) => {
		console.error('Error loading PDF:', error);
		setPdfError('Failed to load PDF');
	};

	// Update canvas dimensions when container size changes
	useEffect(() => {
		const updateCanvasSize = () => {
			const container = canvasRef.current?.parentElement;
			if (!container) return;

			const containerWidth = container.clientWidth;
			const containerHeight = container.clientHeight;

			// Calculate scale to fit container while maintaining aspect ratio
			const scale = Math.min(
				containerWidth / canvasDimensions.width,
				containerHeight / canvasDimensions.height
			);

			// Update canvas style to fit container
			if (canvasRef.current) {
				canvasRef.current.style.width = `${canvasDimensions.width * scale}px`;
				canvasRef.current.style.height = `${canvasDimensions.height * scale}px`;
			}
		};

		updateCanvasSize();
		window.addEventListener('resize', updateCanvasSize);
		return () => window.removeEventListener('resize', updateCanvasSize);
	}, [canvasDimensions]);

	// Add date and neighbourhood title display
	const renderLetterheadContent = () => {
		return (
			<div
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					zIndex: 2
				}}
			>
				{letterheadSettings.showDateOnHeader && (
					<div
						style={{
							position: 'absolute',
							top: '20px',
							right: '20px',
							fontSize: '14px'
						}}
					>
						{new Date().toLocaleDateString()}
					</div>
				)}
				{letterheadSettings.showYourNeighbourhoodTitle && (
					<div
						style={{
							position: 'absolute',
							top: letterheadSettings.startLetterFromTopPixel + 'px',
							left: '20px',
							fontSize: '16px',
							fontWeight: 'bold'
						}}
					>
						Your Neighbourhood
					</div>
				)}
			</div>
		);
	};

	const handleLetterheadUpload = async (event) => {
		setIsUploadingLetterhead(true);
		try {
			const file = event.target.files[0];
			if (!file) return;

			if (!file.name.toLowerCase().endsWith('.pdf')) {
				setUploadError('Only PDF files are allowed');
				return;
			}

			const formData = new FormData();
			formData.append('file', file);

			const response = await axios.post('/api/letterheads/upload', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			if (response.data.success) {
				// Add the new letterhead to the list
				setLetterheads((prev) => [
					...prev,
					{
						fileName: response.data.fileName,
						displayName: response.data.displayName
					}
				]);

				// Select the newly uploaded letterhead for the current page
				await handleLetterheadSelect(response.data.fileName, currentPage);
				await handleLetterheadSelect(response.data.fileName, currentPage);

				setUploadError(null);
			}
		} catch (error) {
			console.error('Error uploading letterhead:', error);
			setUploadError(error.response?.data?.error || 'Failed to upload letterhead');
		} finally {
			setIsUploadingLetterhead(false);
		}

		// Reset the file input
		event.target.value = '';
	};

	// Add new page management functions
	const addNewPage = () => {
		const defaultDims = { width: 612, height: 792 };
		setPages((prev) => [
			...prev,
			{
				elements: [],
				letterhead: null,
				letterheadSettings: {
					showDateOnHeader: false,
					startLetterFromTopPixel: 62,
					showYourNeighbourhoodTitle: true
				},
				dimensions: defaultDims
			}
		]);
		setCurrentPage(pages.length);
	};

	const removePage = (pageIndex) => {
		if (pages.length <= 1) return; // Don't remove the last page
		setPages((prev) => prev.filter((_, index) => index !== pageIndex));
		if (currentPage >= pageIndex) {
			setCurrentPage(Math.max(0, currentPage - 1));
		}
	};

	const handlePageChange = (newPageIndex) => {
		setCurrentPage(newPageIndex);
		setSelectedId(null);
		setShowFormattingToolbar(false);
		setEditingTextId(null);
		// Sync canvas size
		syncCanvasToCurrentPage(pages, newPageIndex);
	};

	// When pages or currentPage changes, sync canvas size
	useEffect(() => {
		syncCanvasToCurrentPage(pages, currentPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pages, currentPage]);

	// Helper to extract PDF page size
	const extractPdfPageSize = async (pdfDataUrl) => {
		try {
			const loadingTask = pdfjs.getDocument(pdfDataUrl);
			const pdf = await loadingTask.promise;
			const page = await pdf.getPage(1);
			const viewport = page.getViewport({ scale: 1 });
			return { width: viewport.width, height: viewport.height };
		} catch (e) {
			console.error('Failed to extract PDF page size', e);
			return { width: 612, height: 792 };
		}
	};

	// Update canvas size to match current page's dimensions
	const syncCanvasToCurrentPage = (pagesArr, pageIdx) => {
		const dims = pagesArr[pageIdx]?.dimensions || { width: 612, height: 792 };
		setCanvasDimensions({ width: dims.width, height: dims.height });
		setTempDimensions({ width: dims.width, height: dims.height });
		// Removed recursive/duplicate call to syncCanvasToCurrentPage
	};

	// When pages or currentPage changes, sync canvas size
	useEffect(() => {
		syncCanvasToCurrentPage(pages, currentPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pages, currentPage]);

	return (
		<div className={classes.root} ref={containerRef}>
			{(isSaving || isUploadingLetterhead) && (
				<div className={classes.loadingOverlay}>
					<CircularProgress />
				</div>
			)}
			{showTemplateSelector ? (
				<>
					<TemplateSelector
						onTemplateSelect={handleTemplateSelect}
						onNewTemplate={handleNewTemplate}
					/>
					{isCreatingTemplate && (
						<div className={classes.loadingOverlay}>
							<CircularProgress />
						</div>
					)}
				</>
			) : (
				<div className={classes.mainContainer}>
					{/* Sidebar */}
					<div className={classes.sidebar}>
						<div className={classes.sidebarSection}>
							<Typography className={classes.sidebarTitle}>Template</Typography>
							<Button
								className={classes.sidebarButton}
								startIcon={<ArrowBackIcon />}
								onClick={handleBackToTemplates}
							>
								BACK TO TEMPLATES
							</Button>
						</div>
						<div className={classes.sidebarSection}>
							<Typography className={classes.sidebarTitle}>Letterhead</Typography>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddPhotoAlternateIcon />}
								onClick={() => fileInputRef.current?.click()}
								fullWidth
							>
								Upload Letterhead
							</Button>
							<input
								type="file"
								ref={fileInputRef}
								hidden
								accept=".pdf"
								onChange={handleLetterheadUpload}
							/>
							{uploadError && (
								<Typography
									color="error"
									style={{ fontSize: '0.75rem', marginTop: '4px' }}
								>
									{uploadError}
								</Typography>
							)}
							{letterhead && (
								<>
									<FormControlLabel
										control={
											<Switch
												checked={letterheadSettings.showDateOnHeader}
												onChange={(e) =>
													setLetterheadSettings((prev) => ({
														...prev,
														showDateOnHeader: e.target.checked
													}))
												}
												disabled={true}
												style={{ display: 'none' }}
											/>
										}
										label="Show Date on Header"
										style={{ display: 'none' }}
									/>
									<FormControlLabel
										control={
											<Switch
												checked={
													letterheadSettings.showYourNeighbourhoodTitle
												}
												onChange={(e) =>
													setLetterheadSettings((prev) => ({
														...prev,
														showYourNeighbourhoodTitle: e.target.checked
													}))
												}
												disabled={true}
												style={{ display: 'none' }}
											/>
										}
										label="Show Neighbourhood Title"
										style={{ display: 'none' }}
									/>
								</>
							)}
						</div>
						<div className={classes.sidebarSection}>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('qrCode')}
							>
								QR CODE
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('cma')}
							>
								CMA SECTION
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<TodayIcon />}
								onClick={() => addElement('dynamicText')}
							>
								DYNAMIC TEXT
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<MapIcon />}
								onClick={() => addElement('map')}
							>
								MAP
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<HomeIcon />}
								onClick={() => addElement('address')}
							>
								ADDRESS
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<TodayIcon />}
								onClick={() => addElement('date')}
							>
								DATE
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<BarChartIcon />}
								onClick={() => addElement('marketStat')}
							>
								MARKET STATS
							</Button>
						</div>
						<div className={classes.sidebarSection}>
							<Typography className={classes.sidebarTitle}>
								Development Analysis
							</Typography>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('plexCount')}
							>
								PLEX COUNT
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('squareFootage')}
							>
								SQUARE FOOTAGE
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('constructionCosts')}
							>
								CONSTRUCTION COSTS
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('propertyValue')}
							>
								PROPERTY VALUE
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('mortgageInterest')}
							>
								MORTGAGE INTEREST
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('revenueProjection')}
							>
								REVENUE PROJECTION
							</Button>
							<Button
								className={classes.sidebarButton}
								startIcon={<AddCircleIcon />}
								onClick={() => addElement('profitCalculation')}
							>
								PROFIT CALCULATION
							</Button>
						</div>
					</div>

					{/* Main Content Area */}
					<div className={classes.contentArea}>
						{/* Top Toolbar */}
						<div className={classes.topToolbar}>
							<div className={classes.toolbarGroup}>
								<IconButton
									onClick={handleUndo}
									disabled={currentIndex <= 0}
									size="small"
									className={classes.toolbarButton}
								>
									<UndoIcon fontSize="small" />
								</IconButton>
								<IconButton
									onClick={handleRedo}
									disabled={currentIndex >= history.length - 1}
									size="small"
									className={classes.toolbarButton}
								>
									<RedoIcon fontSize="small" />
								</IconButton>
							</div>

							{/* Page Navigation */}
							<div className={classes.toolbarGroup}>
								<IconButton
									onClick={() => handlePageChange(currentPage - 1)}
									disabled={currentPage === 0}
									size="small"
									className={classes.toolbarButton}
								>
									<ArrowBackIcon fontSize="small" />
								</IconButton>
								<Typography variant="body2" style={{ margin: '0 8px' }}>
									Page {currentPage + 1} of {pages.length}
								</Typography>
								<IconButton
									onClick={() => handlePageChange(currentPage + 1)}
									disabled={currentPage === pages.length - 1}
									size="small"
									className={classes.toolbarButton}
								>
									<ArrowForwardIcon fontSize="small" />
								</IconButton>
								<IconButton
									onClick={addNewPage}
									size="small"
									className={classes.toolbarButton}
								>
									<AddIcon fontSize="small" />
								</IconButton>
								<IconButton
									onClick={() => removePage(currentPage)}
									disabled={pages.length <= 1}
									size="small"
									className={classes.toolbarButton}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>

							<div className={classes.toolbarGroup}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleSave}
									className={classes.toolbarButton}
									size="small"
								>
									Save
								</Button>
							</div>
						</div>

						{/* Canvas Area */}
						{isLoadingTemplate ? (
							<div className={classes.loadingOverlay}>
								<CircularProgress />
							</div>
						) : (
							<div className={classes.canvasContainer} onClick={handleCanvasClick}>
								{isPdfLoading && (
									<div className={classes.loadingOverlay}>
										<CircularProgress />
									</div>
								)}

								{pdfError && (
									<div className={classes.errorOverlay}>
										<Typography color="error">{pdfError}</Typography>
									</div>
								)}

								<div
									style={{
										position: 'relative',
										width: canvasDimensions.width,
										height: canvasDimensions.height,
										backgroundColor: 'white'
									}}
								>
									{pages[currentPage].letterhead && (
										<>
											<div className={classes.pdfContainer}>
												<Document
													file={pages[currentPage].letterhead.url}
													onLoadSuccess={onDocumentLoadSuccess}
													onLoadError={onDocumentLoadError}
													loading={
														<div className={classes.loadingOverlay}>
															<CircularProgress />
														</div>
													}
													error={
														<div className={classes.errorOverlay}>
															<Typography color="error">
																Failed to load PDF
															</Typography>
														</div>
													}
												>
													<Page
														pageNumber={1}
														width={canvasDimensions.width}
														height={canvasDimensions.height}
														renderTextLayer={false}
														renderAnnotationLayer={false}
													/>
												</Document>
											</div>
											{renderLetterheadContent()}
										</>
									)}

									<div className={classes.elementsContainer}>
										{pages[currentPage].elements.map((element) =>
											renderElement(element)
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Keep your existing dialogs and snackbars */}
			<Snackbar
				open={saveSuccess}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					onClose={handleCloseSnackbar}
					severity="success"
				>
					Template saved successfully!
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={!!saveError}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					onClose={handleCloseSnackbar}
					severity="error"
				>
					{saveError}
				</MuiAlert>
			</Snackbar>

			<Popover
				open={Boolean(dynamicTextAnchorEl)}
				anchorEl={dynamicTextAnchorEl}
				onClose={handleDynamicTextClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left'
				}}
			>
				<List>
					{dynamicTextTypes.map((type) => (
						<ListItem
							button
							key={type.type}
							onClick={() => handleDynamicTypeSelect(type.type)}
						>
							<ListItemIcon>{React.createElement(type.icon)}</ListItemIcon>
							<ListItemText primary={type.label} />
						</ListItem>
					))}
				</List>
			</Popover>
		</div>
	);
};

export default LetterDesigner;
